/**
 * WidgetRuntime — the in-phone widget runtime, mounted inside PhonePreview.
 *
 * This is the old /widget/use dashboard runtime absorbed into Widget Studio
 * (the standalone Widget Use surface was removed). One component, two data
 * modes, switched by the studio preview's User Data / Mock Data control:
 *
 *   dataMode='user'  — "really use like in app": the signed-in user's real
 *     widget_instance feed (signed media URLs), capture overlay, floating
 *     nav pill (widget ⇄ sessions) + chat FAB, real session jumps, host
 *     item-options sheet with Delete, attach/append, log_data — the full
 *     bridge contract the iPhone app implements, so testing on web fully
 *     mimics/validates the app.
 *
 *   dataMode='mock'  — example_data.json injected as __WIDGET_DATA__; the
 *     full UX renders (item data view included) but anything that would
 *     CHANGE data or TRIGGER a session (capture, log_data, delete, attach,
 *     session jumps) is gated behind a toast pointing at User Data.
 *
 * HTML source follows the studio's preview tag (scratch | dev | release) so
 * the designer can exercise either mode against any version, with fallback
 * to the other committed tag when a file (e.g. data_view.html) is missing
 * from the selected one.
 *
 * The per-item data view is URL-synced (?item=<entry_id>) exactly like the
 * old dashboard so session ↔ data-view round trips survive navigation.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, History, LayoutGrid, MessageCircle, MoreHorizontal, Pencil, Plus, Search, Trash2, UserRound } from 'lucide-react';
import { api } from '../../services/api';
import { widgetApi, widgetInstances } from '../../services/widgetApi';
import WidgetCaptureOverlay from './WidgetCaptureOverlay';
import { mergePendingGridAdds } from './pendingGridProjection';
import { hasHiddenTags, stripHiddenTags, stripHiddenTagsDeep } from '../../utils/text';

// SWR cache — paint the last-known entries / sessions immediately on mount,
// then revalidate in the background. Keyed per instance (user mode only).
const CACHE_NS = 'vi:widget-runtime:v1';
function cacheGet(key) {
  try {
    const raw = localStorage.getItem(`${CACHE_NS}:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed.value ?? null;
  } catch { return null; }
}
function cacheSet(key, value) {
  try {
    localStorage.setItem(`${CACHE_NS}:${key}`, JSON.stringify({ value, at: Date.now() }));
  } catch { /* quota — non-fatal */ }
}

const T = {
  bg: '#F2F2F7',
  card: '#FFFFFF',
  text: '#1C1C1E',
  textMut: '#8E8E93',
  cell: '#E5E5EA',
  accent: '#9A9589',
  accentLight: '#F0EFEA',
  healthGreen: '#34C759',
  warn: '#FF9500',
};
const FONT_UI = "-apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif";

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const ts = typeof iso === 'number' ? iso : Date.parse(iso);
    if (!Number.isFinite(ts)) return '';
    const diff = Date.now() - ts;
    if (diff < 60_000) return 'just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return new Date(ts).toLocaleString();
  } catch { return ''; }
}

function statusColor(status) {
  switch ((status || '').toLowerCase()) {
    case 'completed':
    case 'success': return T.healthGreen;
    case 'failed':
    case 'error': return '#FF453A';
    case 'running':
    case 'queued':
    case 'streaming': return T.warn;
    default: return T.textMut;
  }
}

// An `open_page` host overlay is "session-bound" (draws the top-right Session
// badge, wires session-scoped chrome) for `session_view` (canonical) and its
// backward-compat alias `data_view`; everything else is a collection surface
// (`index` — back chevron only). App parity: `_WidgetPagePage.pageType`.
const isSessionBoundPageType = (t) => t === 'session_view' || t === 'data_view';

// The host-side bridge script injected into the widget iframe — verbatim the
// contract the iPhone app's WebView shell implements (long-press → host
// item_options sheet; click delegation for every data-bridge-action).
const BRIDGE_SCRIPT = `
(function(){
  var LP_MS = 500, LP_MOVE = 10;
  var lpTimer = null, lpStart = null, lpEntry = null, lpFired = false;
  function itemFromTarget(t){
    if (!t || typeof t.closest !== 'function') return null;
    var grid = t.closest('[data-remove-grid]');
    if (grid) {
      var gridId = grid.getAttribute('data-remove-grid') || '';
      if (gridId) {
        var gridPayload = { grid_id: gridId };
        var pendingSessionId = grid.getAttribute('data-pending-session-id') || '';
        if (pendingSessionId) gridPayload.pending_session_id = pendingSessionId;
        return gridPayload;
      }
    }
    var el = t.closest('[data-bridge-action="open_item"],[data-item-id]');
    if (!el) return null;
    var act = el.getAttribute('data-bridge-action');
    if (act === 'attach_item_image' || act === 'open_capture' || act === 'open_session' || act === 'item_action') return null;
    var id = el.getAttribute('data-item-id');
    return id ? { entry_id: id } : null;
  }
  function postOptions(it){
    try { var s = window.getSelection && window.getSelection(); if (s && s.removeAllRanges) s.removeAllRanges(); } catch (_) {}
    try { parent.postMessage({ source: 'NewEyesWidget', type: 'item_options', payload: it }, '*'); } catch (_) {}
  }
  function clearLP(){ if (lpTimer) { clearTimeout(lpTimer); lpTimer = null; } lpStart = null; lpEntry = null; }
  function onPointerDown(e){
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    var it = itemFromTarget(e.target);
    if (!it) return;
    lpEntry = it; lpStart = { x: e.clientX, y: e.clientY }; lpFired = false;
    lpTimer = setTimeout(function(){ lpTimer = null; if (lpEntry) { lpFired = true; postOptions(lpEntry); } }, LP_MS);
  }
  function onPointerMove(e){
    if (!lpStart) return;
    if (Math.abs(e.clientX - lpStart.x) > LP_MOVE || Math.abs(e.clientY - lpStart.y) > LP_MOVE) clearLP();
  }
  function onPointerUp(){ clearLP(); }
  function onContextMenu(e){
    var it = itemFromTarget(e.target);
    if (!it) return;
    e.preventDefault(); e.stopPropagation();
    lpFired = true;
    postOptions(it);
  }
  function onClick(e){
    if (lpFired) { lpFired = false; e.preventDefault(); e.stopPropagation(); return; }
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    var cap = t.closest('[data-bridge-action="open_capture"],[data-add-capture],.widget-add,.add-entry');
    if (cap) {
      e.preventDefault();
      e.stopPropagation();
      var payload = {};
      var kind = cap.getAttribute('data-widget-kind') || cap.getAttribute('data-kind');
      if (kind) payload.kind = kind;
      var capPrompt = cap.getAttribute('data-prompt') || '';
      if (capPrompt) payload.prompt = capPrompt;
      var capTarget = cap.getAttribute('data-target-page') || cap.getAttribute('data-after-page');
      if (capTarget) payload.target_page = capTarget;
      try { parent.postMessage({ source: 'NewEyesWidget', type: 'open_capture', payload: payload }, '*'); } catch (_) {}
      return;
    }
    var pg = t.closest('[data-bridge-action="open_page"]');
    if (pg) {
      e.preventDefault();
      e.stopPropagation();
      var pgPayload = { file: pg.getAttribute('data-page-file') || pg.getAttribute('data-file') || '' };
      var pgType = pg.getAttribute('data-page-type');
      if (pgType) pgPayload.type = pgType;
      if (pgPayload.file) { try { parent.postMessage({ source: 'NewEyesWidget', type: 'open_page', payload: pgPayload }, '*'); } catch (_) {} }
      return;
    }
    var ask = t.closest('[data-bridge-action="open_main_session"]');
    if (ask) {
      e.preventDefault();
      e.stopPropagation();
      var askPayload = {};
      var ap = ask.getAttribute('data-prompt') || '';
      if (ap) askPayload.prompt = ap;
      var aq = ask.getAttribute('data-quick-log');
      if (aq === 'true' || aq === '1') askPayload.quick_log = true;
      try { parent.postMessage({ source: 'NewEyesWidget', type: 'open_main_session', payload: askPayload }, '*'); } catch (_) {}
      return;
    }
    var att = t.closest('[data-bridge-action="attach_item_image"]');
    if (att) {
      e.preventDefault();
      e.stopPropagation();
      try {
        parent.postMessage({
          source: 'NewEyesWidget',
          type: 'attach_item_image',
          payload: { entry_id: att.getAttribute('data-item-id') },
        }, '*');
      } catch (_) {}
      return;
    }
    var ia = t.closest('[data-bridge-action="item_action"]');
    if (ia) {
      e.preventDefault();
      e.stopPropagation();
      var iaAct = ia.getAttribute('data-item-action') || '';
      if (!iaAct) return;
      var iaPayload = { action: iaAct };
      var iaEid = ia.getAttribute('data-item-id') || ia.getAttribute('data-entry-id');
      if (iaEid) iaPayload.entry_id = iaEid;
      var iaLcid = ia.getAttribute('data-log-card-id');
      if (iaLcid) iaPayload.log_card_id = iaLcid;
      var iaSid = ia.getAttribute('data-session-id');
      if (iaSid) iaPayload.session_id = iaSid;
      var iaIdx = ia.getAttribute('data-img-index');
      if (iaIdx !== null && iaIdx !== '') iaPayload.image_index = iaIdx;
      var iaImg = ia.getAttribute('data-save-image');
      if (!iaImg) { try { var im = ia.querySelector && ia.querySelector('img'); iaImg = im ? (im.currentSrc || im.getAttribute('src')) : null; } catch (_) {} }
      if (iaImg) iaPayload.image_url = iaImg;
      var iaFile = ia.getAttribute('data-file-url');
      if (iaFile) iaPayload.file_url = iaFile;
      var iaFn = ia.getAttribute('data-filename');
      if (iaFn) iaPayload.filename = iaFn;
      try { parent.postMessage({ source: 'NewEyesWidget', type: 'item_action', payload: iaPayload }, '*'); } catch (_) {}
      return;
    }
    var item = t.closest('[data-bridge-action="open_item"],[data-item-id]');
    if (item && item.getAttribute('data-item-id')) {
      // Widget-internal handler takes precedence on CLICK: an element marked
      // with the widget's own data-act (e.g. data_view look pins —
      // data-act="openLook" + data-item-id, where data-item-id is only the
      // host LONG-PRESS anchor) handles its own taps; hijacking them here
      // killed every in-widget tap on the data view. Host-owned clicks are
      // explicit data-bridge-action="open_item" or a bare data-item-id.
      if (!item.getAttribute('data-bridge-action') && item.hasAttribute('data-act')) return;
      e.preventDefault();
      e.stopPropagation();
      try {
        parent.postMessage({
          source: 'NewEyesWidget',
          type: 'open_item',
          payload: { entry_id: item.getAttribute('data-item-id') },
        }, '*');
      } catch (_) {}
      return;
    }
    var ses = t.closest('[data-bridge-action="open_session"],[data-session-id]');
    if (ses && !ses.getAttribute('data-bridge-action') && ses.hasAttribute('data-act')) return;
    if (ses && (ses.getAttribute('data-session-id') || ses.getAttribute('data-log-card-id'))) {
      e.preventDefault();
      e.stopPropagation();
      try {
        parent.postMessage({
          source: 'NewEyesWidget',
          type: 'open_session',
          payload: {
            session_id: ses.getAttribute('data-session-id') || undefined,
            log_card_id: ses.getAttribute('data-log-card-id') || undefined,
            prompt: ses.getAttribute('data-prompt') || undefined,
          },
        }, '*');
      } catch (_) {}
    }
  }
  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('pointermove', onPointerMove, true);
  document.addEventListener('pointerup', onPointerUp, true);
  document.addEventListener('pointercancel', onPointerUp, true);
  document.addEventListener('contextmenu', onContextMenu, true);
  document.addEventListener('click', onClick, true);

  // ── e2e test bridge (gated on window.__VI_E2E__, set only when the studio
  //    URL carries ?e2e=1). Because the preview iframe is sandbox="allow-scripts"
  //    (opaque cross-origin), browser automation can't reach elements INSIDE it.
  //    This lets a driver in the top frame post commands straight to this
  //    iframe's contentWindow to click/read the widget. No-op in normal use. ──
  function e2eReply(id, payload){ try { parent.postMessage({ source: 'NewEyesWidget', type: 'e2e_result', id: id, payload: payload }, '*'); } catch (_) {} }
  window.addEventListener('message', function(ev){
    if (!window.__VI_E2E__) return;
    var d = ev.data;
    if (!d || d.source !== 'NewEyesApp' || typeof d.type !== 'string' || d.type.indexOf('e2e_') !== 0) return;
    try {
      if (d.type === 'e2e_click') {
        var el = d.selector ? document.querySelector(d.selector) : null;
        if (!el) { e2eReply(d.id, { ok: false, error: 'selector not found: ' + d.selector }); return; }
        try { el.scrollIntoView({ block: 'center' }); } catch (_) {}
        el.click();
        e2eReply(d.id, { ok: true });
        return;
      }
      if (d.type === 'e2e_text') {
        var nodes = [].slice.call(document.querySelectorAll(d.selector || '*'));
        e2eReply(d.id, { ok: true, count: nodes.length, text: nodes.slice(0, 60).map(function(n){ return (n.textContent || '').trim(); }) });
        return;
      }
      if (d.type === 'e2e_state') {
        e2eReply(d.id, { ok: true, widget_data: (window.__WIDGET_DATA__ || null),
          tabs: [].slice.call(document.querySelectorAll('[data-tab]')).map(function(n){ return n.getAttribute('data-tab'); }) });
        return;
      }
      if (d.type === 'e2e_dump') {
        var root = document.getElementById('app') || document.body;
        e2eReply(d.id, { ok: true, html: (root ? root.innerHTML : '').slice(0, (d.max || 20000)) });
        return;
      }
      e2eReply(d.id, { ok: false, error: 'unknown e2e type: ' + d.type });
    } catch (err) { e2eReply(d.id, { ok: false, error: String((err && err.message) || err) }); }
  });
})();
`;

// Mouse-swipe shim (WEB ONLY — the iPhone WebView shell has real touch). Widget
// bundles build carousels for TOUCH (design-kit convention: a translateX slider
// with touchstart/touchend swipe, or a horizontal scroll container) — so on a
// desktop mouse the carousel can't be dragged, only its arrows/dots work
// (my-wardrobe photo.html). Rather than teach every bundle a mouse handler, the
// host translates a horizontal mouse-DRAG into the gesture the bundle already
// understands: drag-scroll for an overflow-x container, else synthesized touch
// events for a touch-handler slider. Gated to fine-pointer (mouse) devices so a
// touchscreen's native touch is never doubled; a plain click never synthesizes
// (only a clearly-horizontal drag does), so taps/arrows/thumbnails are untouched.
const MOUSE_SWIPE_SHIM = `
(function(){
  try { if (!window.matchMedia || !matchMedia('(pointer: fine)').matches) return; } catch(_) { return; }
  var DRAG = 8; // px of horizontal travel before a click becomes a drag
  function scrollableX(el){
    for (var n = el; n && n !== document.body && n.nodeType === 1; n = n.parentElement){
      try {
        var ox = getComputedStyle(n).overflowX;
        if ((ox === 'auto' || ox === 'scroll') && n.scrollWidth > n.clientWidth + 1) return n;
      } catch(_) {}
    }
    return null;
  }
  function synth(type, x, y, target){
    if (!target) return;
    var ev, list = [{ identifier: 1, target: target, clientX: x, clientY: y, pageX: x, pageY: y, screenX: x, screenY: y }];
    var active = (type === 'touchend') ? [] : list;
    try {
      var t = new Touch(list[0]);
      ev = new TouchEvent(type, { bubbles: true, cancelable: true, composed: true,
        touches: (type === 'touchend') ? [] : [t], targetTouches: (type === 'touchend') ? [] : [t], changedTouches: [t] });
    } catch(_) {
      ev = new Event(type, { bubbles: true, cancelable: true });
      Object.defineProperty(ev, 'touches', { value: active });
      Object.defineProperty(ev, 'targetTouches', { value: active });
      Object.defineProperty(ev, 'changedTouches', { value: list });
    }
    try { target.dispatchEvent(ev); } catch(_) {}
  }
  var armed = false, dragging = false, sx = 0, sy = 0, tgt = null, scEl = null, scLeft = 0;
  document.addEventListener('mousedown', function(e){
    if (e.button !== 0) return;
    armed = true; dragging = false; sx = e.clientX; sy = e.clientY; tgt = e.target;
    scEl = scrollableX(e.target); scLeft = scEl ? scEl.scrollLeft : 0;
  }, true);
  document.addEventListener('mousemove', function(e){
    if (!armed) return;
    var dx = e.clientX - sx, dy = e.clientY - sy;
    if (!dragging){
      if (Math.abs(dx) < DRAG || Math.abs(dx) <= Math.abs(dy)) return; // wait for a clearly-horizontal drag
      dragging = true;
      if (!scEl) synth('touchstart', sx, sy, tgt);
    }
    if (scEl) scEl.scrollLeft = scLeft - dx;
    else synth('touchmove', e.clientX, e.clientY, tgt);
    e.preventDefault();
  }, true);
  document.addEventListener('mouseup', function(e){
    if (armed && dragging){
      if (!scEl) synth('touchend', e.clientX, e.clientY, tgt);
      // a drag must NOT also fire the tap (e.g. open the fullscreen viewer)
      var kill = function(ev){ ev.stopPropagation(); ev.preventDefault(); document.removeEventListener('click', kill, true); };
      document.addEventListener('click', kill, true);
      setTimeout(function(){ document.removeEventListener('click', kill, true); }, 350);
    }
    armed = false; dragging = false; scEl = null; tgt = null;
  }, true);
})();
`;

// e2e driver mode: enabled ONLY when the studio URL carries ?e2e=1. Injected as
// window.__VI_E2E__ into the preview blob so the in-iframe bridge (above) accepts
// test commands. Off (and inert) in all normal use.
function e2eEnabled() {
  try { return new URLSearchParams(window.location.search).get('e2e') === '1'; } catch { return false; }
}

// Defensive example_data.json parse — designer files sometimes carry // line
// comments or trailing commas; strict JSON.parse rejects those and the
// preview would blank silently. One fixup pass, then {}.
function parseExampleData(raw) {
  const tryParse = (s) => { try { return JSON.parse(s); } catch { return null; } };
  let parsed = tryParse(raw || '{}');
  if (parsed == null) {
    const stripped = String(raw || '')
      .replace(/\/\*[\s\S]*?\*\//g, '')
      .replace(/(^|[^:])\/\/.*$/gm, '$1')
      .replace(/,\s*(\]|\})/g, '$1');
    parsed = tryParse(stripped);
  }
  return (parsed && typeof parsed === 'object') ? parsed : {};
}

export default function WidgetRuntime({
  widgetId,
  widget,              // widget summary (tags + title) from the studio editor
  dataMode,            // 'user' | 'mock'
  requestedInstanceId, // ?instance= deep link (user mode)
  previewTag,          // 'scratch' | 'dev' | 'release'
  previewVersion,      // exact version_label to preview (studio viewer) → overrides the tag walk
  pinnedSessionId,     // studio scratch session (scratch tag reads)
  previewSha,          // cache-bust key — autosave/SSE bumps reload the HTML
  appearance,          // 'light' | 'dark' | 'auto'
  // When provided (studio surface), the "Chat with widget" FAB opens the main
  // session view IN PLACE via this callback ({sessionId, ask, widgetId,
  // instanceId, tag}) instead of navigating to /playground/chat. Optional →
  // any other surface that omits it keeps the navigate behavior.
  onOpenMainSession,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isUser = dataMode === 'user';

  // ── Instance resolution (user mode) ─────────────────────────────────────
  const [instances, setInstances] = useState(null);   // null = loading
  const [instanceId, setInstanceId] = useState(requestedInstanceId || null);
  const instance = useMemo(
    () => (instances || []).find((i) => i.instance_id === instanceId) || null,
    [instances, instanceId],
  );
  const [instantiateBusy, setInstantiateBusy] = useState(false);
  const [templateTitle, setTemplateTitle] = useState('');

  // Both modes resolve the user's instances: user mode binds the feed to one;
  // Example (Discovery) mode needs to know whether "Use this Widget" should
  // adopt first or jump straight to User Data.
  const reloadInstances = useCallback(async () => {
    if (!widgetId) return;
    try {
      const res = await widgetInstances.list();
      const mine = (res?.instances || []).filter((i) => i.widget_id === widgetId);
      setInstances(mine);
      setInstanceId((prev) => {
        if (prev && mine.some((i) => i.instance_id === prev)) return prev;
        if (requestedInstanceId && mine.some((i) => i.instance_id === requestedInstanceId)) return requestedInstanceId;
        return mine[0]?.instance_id || null;
      });
    } catch (e) {
      console.warn('[widget-runtime] instance list failed:', e);
      setInstances([]);
    }
  }, [isUser, widgetId, requestedInstanceId]);

  useEffect(() => { reloadInstances(); }, [reloadInstances]);

  // App-parity adoption state:
  //   adopted    — the signed-in user has an instance bound in USER mode →
  //                full chrome (nav pill + chat FAB + "⋯" menu).
  //   useExample — what the phone renders comes from example_data.json:
  //                Example mode always, and user mode while NOT adopted
  //                (the app's Discovery preview — example content with the
  //                "Use this Widget" CTA, never an auto-instantiate).
  const adopted = isUser && instances != null && !!instance;
  const useExample = !isUser || (instances != null && !instance);
  const [ctaError, setCtaError] = useState(null);

  const jumpToUserData = useCallback((iid) => {
    const params = new URLSearchParams(location.search);
    params.set('data', 'user');
    if (iid) params.set('instance', iid); else params.delete('instance');
    params.delete('item');
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [navigate, location.pathname, location.search]);

  // "Use this Widget" — the ONLY way the runtime adopts (mirrors the app's
  // CTA: adoption happens on tap, never implicitly). In Example mode an
  // already-adopted widget just jumps to User Data.
  const handleUseThisWidget = useCallback(async () => {
    if (instantiateBusy) return;
    if (!isUser && instance) { jumpToUserData(instance.instance_id); return; }
    const isTemplate = widget?.widget_type === 'template';
    const title = templateTitle.trim();
    if (isTemplate && !title) { setCtaError('Name your collection first'); return; }
    setInstantiateBusy(true);
    setCtaError(null);
    try {
      const res = await widgetInstances.instantiate(widgetId, isTemplate ? { title } : {});
      await reloadInstances();
      if (res?.instance_id) setInstanceId(res.instance_id);
      if (!isUser) jumpToUserData(res?.instance_id);
    } catch (e) {
      console.warn('[widget-runtime] use-this-widget failed:', e);
      setCtaError(e?.message || String(e));
    } finally {
      setInstantiateBusy(false);
    }
  }, [widgetId, widget?.widget_type, templateTitle, instantiateBusy, reloadInstances, isUser, instance, jumpToUserData]);

  // ── Data state ───────────────────────────────────────────────────────────
  const [entries, setEntries] = useState([]);
  const [entriesLoaded, setEntriesLoaded] = useState(false);
  // Pending quick-log placeholders (see reloadData) — [{session_id, log_card_id,
  // kind, payload, widget_title, options_count, created_at}].
  const [pendingLogs, setPendingLogs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [mockPayload, setMockPayload] = useState(null); // parsed example_data.json
  const [view, setView] = useState('widget');           // widget | sessions | item | page
  const [itemEntryId, setItemEntryId] = useState(null);
  // Host-template scheme: a standalone bundle page (e.g. gallery.html) reached
  // via the `open_page` bridge. `pageType` picks the host overlay decoupled
  // from the filename ('index' = no session badge, own back to home;
  // 'session_view' = session-bound chrome — draws the top-right Session badge;
  // 'data_view' is a backward-compat alias of 'session_view'). `pageArgsRef` is
  // a one-shot seed (e.g. a pending placeholder / the source session_id for the
  // Session badge) injected as __WIDGET_DATA__.page_args.
  const [pageFile, setPageFile] = useState(null);
  const [pageType, setPageType] = useState('index');
  const pageArgsRef = useRef(null);
  // open_capture `target_page`: after the send dispatches (pending skeleton
  // registered) the host lands on this page so the placeholder shows there.
  const captureTargetPageRef = useRef(null);

  // URL → item/page-view sync (?item=<entry_id> | ?page=<file>): initial mount,
  // open_item/open_page pushes, and browser back/forward all land here.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const eid = params.get('item');
    const pg = params.get('page');
    if (eid) {
      setItemEntryId(eid);
      setView('item');
    } else if (pg) {
      setPageFile(pg);
      setView('page');
    } else {
      setItemEntryId(null);
      setPageFile(null);
      setView((v) => (v === 'item' || v === 'page' ? 'widget' : v));
    }
  }, [location.search]);

  // `?item=<entry_id>` and `?page=<file>` are the two mutually-exclusive overlay
  // views. Both public param names stay live — they are the deep-link /
  // back-forward contract — so these two helpers dedupe the *navigation* only,
  // not the params or the render branches. (Flutter unified the two openers into
  // one push path in #3592; the web analog is a URL state machine, so the shared
  // part is the URL write, not a route push.)
  // ⚠️ Declared ABOVE every callback that names them — a `useCallback` referenced
  // by an effect declared above it is a TDZ crash on this route (#2697).
  const openOverlayView = useCallback((param, value) => {
    const params = new URLSearchParams(location.search);
    params.set(param, value);
    params.delete(param === 'item' ? 'page' : 'item');
    navigate(`?${params.toString()}`);
  }, [navigate, location.search]);
  const closeOverlayView = useCallback((param) => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
      return;
    }
    // Deep-linked: strip just this overlay's param, keep ?data/?instance.
    const params = new URLSearchParams(location.search);
    params.delete(param);
    const q = params.toString();
    navigate(q ? `${location.pathname}?${q}` : location.pathname, { replace: true });
  }, [navigate, location.pathname, location.search]);

  const openItemView = useCallback((eid) => openOverlayView('item', eid), [openOverlayView]);
  const closeItemView = useCallback(() => closeOverlayView('item'), [closeOverlayView]);
  const openPageView = useCallback((file, type = 'index', args = null) => {
    pageArgsRef.current = args;
    // Preserve the session-bound literal ('session_view' | 'data_view' alias);
    // collapse anything else to the collection surface ('index').
    setPageType(isSessionBoundPageType(type) ? type : 'index');
    openOverlayView('page', file);
  }, [openOverlayView]);
  const closePageView = useCallback(() => {
    pageArgsRef.current = null;
    closeOverlayView('page');
  }, [closeOverlayView]);

  // The two overlay views share the host chrome rules (no bottom bar, host back
  // closes the overlay, Session badge eligible) — several branches below key off
  // "either one", so name it once.
  const isOverlayView = view === 'item' || view === 'page';

  const [captureOpen, setCaptureOpen] = useState(false);
  // Depop quick add (open_capture payload.quick): the overlay collapses to
  // the photo tile and auto-sends the pick (app parity).
  const [captureQuick, setCaptureQuick] = useState(false);
  // v1.1 sub-view contract: widget is on an internal sub-page → bar hides,
  // host back routes into the widget (host_back).
  const [widgetSubview, setWidgetSubview] = useState(false);
  // Widgets-refine nav: bundle-declared bar middle action
  // ('search' | 'edit_goal' | 'gallery' | null).
  const [navAction, setNavAction] = useState(null);
  // Session the capture should CONTINUE (open_capture payload.session_id) — a
  // session-bound board's "+"/Add Look stays a capture flow but appends into
  // the board's rolling session instead of minting a new one per add.
  const [captureSessionId, setCaptureSessionId] = useState(null);
  // Note text the widget asked to prefill (open_capture data-prompt) — e.g. a
  // board widget's "File this look into board: X" scripted capture.
  const [capturePrefill, setCapturePrefill] = useState(null);
  // Scripted send (open_capture `images`): stored media:// refs the widget
  // pre-attached → headless dispatch (no picker), app-capability tier 0.8.2.
  const [captureImages, setCaptureImages] = useState([]);

  // ── Pending placeholders (the widget "loading" contract) ────────────────
  // The session-list placeholder is pendingRecordings (below — the app-parity
  // Recording… rows); its temp id doubles as the `client_ref` echoed back on
  // the widget_data_changed SSE, so binding survives a lost /exec response
  // and deleting other sessions meanwhile can't mis-bind.
  // pendingOps: rides `__WIDGET_DATA__.pending = {adds, updates, deleting,
  // deleting_photos, deleting_grids}` into the widget HTML so opted-in bundles
  // render skeleton tiles (adds = creating), dim in-flight entries (updates =
  // modifying), and show "Deleting…" tiles for a session-scoped Remove
  // (deleting = a session_id match, deleting_photos = a photo-url match — the
  // fallback for older captures whose source_session_id is empty) or for a
  // single-item long-press delete (deleting_grids = an entry-id match, seeded
  // by deleteItem below). Old bundles ignore the extra fields.
  const [pendingOps, setPendingOps] = useState({ adds: [], updates: [], deleting: [], deleting_photos: [], deleting_grids: [] });
  const addPendingUpdate = useCallback((entryId) => {
    if (!entryId) return;
    setPendingOps((p) => (p.updates.includes(entryId) ? p : { ...p, updates: [...p.updates, entryId] }));
  }, []);
  const clearPendingUpdate = useCallback((entryId) => {
    setPendingOps((p) => (p.updates.includes(entryId)
      ? { ...p, updates: p.updates.filter((x) => x !== entryId) } : p));
  }, []);
  const [itemMenu, setItemMenu] = useState(null);
  const [itemMenuBusy, setItemMenuBusy] = useState(false);
  // Top-right "⋯" overflow (app parity: widget content view + adopted only)
  // → single destructive "Delete this Widget" + confirm dialog.
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [confirmInstanceDelete, setConfirmInstanceDelete] = useState(false);
  const [instanceDeleteBusy, setInstanceDeleteBusy] = useState(false);
  const [mainBusy, setMainBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const showToast = useCallback((msg) => {
    setToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);
  useEffect(() => () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); }, []);
  const mockGate = useCallback(() => {
    showToast('Example data — switch to User Data to do that');
  }, [showToast]);

  const [previewUrl, setPreviewUrl] = useState(null);
  const blobUrlRef = useRef(null);
  // The last merged HTML actually published — identical rebuilds are skipped
  // (see the churn guard in the blob effect).
  const lastHtmlRef = useRef(null);
  // Document identity of the published blob (file + view + user payload) —
  // when it matches and only the data changed, the effect pushes
  // `update_widget_data` into the live iframe instead of rebuilding.
  const docKeyRef = useRef(null);
  const iframeReadyRef = useRef(false);
  const iframeElRef = useRef(null);
  const attachFileRef = useRef(null);
  const attachEntryIdRef = useRef(null);
  const pickMediaFileRef = useRef(null);
  const pickMediaRequestRef = useRef(null);

  // ── User-mode feed ───────────────────────────────────────────────────────
  const reloadData = useCallback(async () => {
    if (!isUser || !instanceId) return;
    try {
      // format=signed: the feed goes into the SANDBOXED iframe — its <img>
      // tiles run from an opaque origin and can't send the SameSite=strict
      // cookie, so the /api/media proxy 401s. Signed URLs need no auth.
      const res = await widgetInstances.data(instanceId, { limit: 100, format: 'signed' });
      const rows = Array.isArray(res) ? res : (res?.entries || []);
      setEntries(rows);
      cacheSet(`entries:${instanceId}`, rows);
      // Unacted pending Log cards from quick-log sessions (server-derived).
      // Host-rendered attention row; disappears once logged/canceled.
      setPendingLogs(Array.isArray(res?.pending_logs) ? res.pending_logs : []);
    } catch (e) {
      console.warn('[widget-runtime] data fetch failed:', e);
    } finally {
      setEntriesLoaded(true);
    }
  }, [isUser, instanceId]);

  // Pending "Recording…" rows (Canon Common-UX #12): capture Send hides the
  // sheet IMMEDIATELY and a transient row appears in the session list under a
  // local temp id (upload + dispatch run in the background); the row is
  // re-keyed to the REAL session id when dispatch resolves. Once the server
  // exposes either the session id or its grid/client ref, the durable server
  // row takes over; a capture that never created a session ages out after 5m.
  const [pendingRecordings, setPendingRecordings] = useState([]);

  const reconcileRecordings = useCallback((serverSessions) => {
    setPendingRecordings((prev) => {
      if (!prev.length) return prev;
      const serverKeys = new Set();
      for (const s of serverSessions || []) {
        const sid = s.id || s.session_id;
        if (sid) serverKeys.add(String(sid));
        if (s.client_ref) serverKeys.add(String(s.client_ref));
        if (s.grid_id) serverKeys.add(String(s.grid_id));
      }
      const now = Date.now();
      return prev.filter((r) => {
        const resolved = serverKeys.has(String(r.id));
        const agedOut = !r.startedAt || (now - r.startedAt) > 5 * 60_000;
        return !resolved && !agedOut;
      });
    });
  }, []);

  const reloadSessions = useCallback(async () => {
    if (!isUser || !instanceId) return;
    try {
      const res = await widgetInstances.sessions(instanceId, { limit: 200 });
      const rows = Array.isArray(res) ? res : (res?.sessions || []);
      reconcileRecordings(rows);
      setSessions(rows);
      cacheSet(`sessions:${instanceId}`, rows);
    } catch (e) {
      console.warn('[widget-runtime] sessions fetch failed:', e);
    }
  }, [isUser, instanceId, reconcileRecordings]);

  // While a Recording… row is pending, poll sessions + feed every 8s (the
  // app's `_ensureRecordingPoll`) — SSE frames are redundant signals.
  const pendingCount = pendingRecordings.length;

  useEffect(() => {
    if (!isUser) return;
    if (!instanceId) { setEntries([]); setSessions([]); setEntriesLoaded(instances != null); return; }
    setEntries(cacheGet(`entries:${instanceId}`) || []);
    setSessions(cacheGet(`sessions:${instanceId}`) || []);
    setEntriesLoaded(cacheGet(`entries:${instanceId}`) != null);
    reloadData();
    reloadSessions();
  }, [isUser, instanceId, instances, reloadData, reloadSessions]);

  useEffect(() => {
    if (!pendingCount) return undefined;
    // SAFETY NET only: live feed updates ride the widget_data_changed SSE
    // now — this slow poll's sole remaining job is the terminal-status
    // reconcile + 5-min age-out when every SSE signal is lost.
    const t = setInterval(() => { reloadSessions(); }, 30_000);
    return () => clearInterval(t);
  }, [pendingCount, reloadSessions]);

  // Capture-dispatch lifecycle (threaded into WidgetCaptureOverlay).
  const onDispatchStarted = useCallback((tempId, title, thumb) => {
    if (!tempId) return;
    setPendingRecordings((prev) => [
      { id: tempId, title, status: 'Recording…', startedAt: Date.now(), recording: true },
      ...prev,
    ]);
    // Matching skeleton add in the widget grid — the temp id IS the
    // client_ref the overlay stamps on the exec context, so the
    // widget_data_changed echo resolves both placeholders.
    setPendingOps((p) => ({
      ...p,
      adds: [{
        client_ref: tempId,
        started_at: Date.now(),
        widget_id: widgetId,
        widget_instance_id: instanceId,
        ...(thumb ? { thumb } : {}),
      }, ...p.adds],
    }));
    // Widget asked (open_capture target_page) to land the result on a specific
    // page (e.g. gallery.html): jump now that the skeleton placeholder exists,
    // so it renders there and the SSE echo resolves it in place.
    const tp = captureTargetPageRef.current;
    if (tp) {
      captureTargetPageRef.current = null;
      openPageView(tp, 'index');
    }
  }, [openPageView, widgetId, instanceId]);
  const onDispatchResolved = useCallback((tempId, sessionId) => {
    if (!sessionId) return;
    setPendingRecordings((prev) => prev.map((r) => (r.id === tempId ? { ...r, id: sessionId } : r)));
    setPendingOps((p) => ({
      ...p,
      adds: p.adds.map((add) => (add.client_ref === tempId ? { ...add, session_id: sessionId } : add)),
    }));
    reloadSessions();
  }, [reloadSessions]);

  // Sessions as rendered: pending Recording… rows first, then the server rows
  // minus any id already pending (no dupes while a row is being reconciled).
  const mergedSessions = useMemo(() => {
    if (!pendingRecordings.length) return sessions;
    const pendingIds = new Set(pendingRecordings.map((r) => String(r.id)));
    return [
      ...pendingRecordings,
      ...(sessions || []).filter((s) => !pendingIds.has(String(s.id || s.session_id))),
    ];
  }, [pendingRecordings, sessions]);

  // SSE hot-refresh (user mode): new capture rows / instance renames land
  // without a remount.
  useEffect(() => {
    if (!isUser || !instanceId) return undefined;
    const viUserId = api.getViUserId?.();
    if (!viUserId) return undefined;
    const es = new EventSource('/api/users/events', { withCredentials: true });
    const scoped = (msg) => msg.widget_instance_id === instanceId || msg.widget_id === widgetId;
    const handleDataAppended = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (scoped(msg)) { reloadData(); reloadSessions(); }
      } catch { /* skip */ }
    };
    const handleInstanceUpdated = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (scoped(msg)) { reloadInstances(); reloadData(); }
      } catch { /* skip */ }
    };
    // Unified live-data event: ANY mutation of this instance's feed (host or
    // agent, append/update/delete) → lazy refetch + resolve the placeholder
    // matching the echoed client_ref.
    const handleDataChanged = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (!scoped(msg)) return;
        if (msg.client_ref) {
          setPendingOps((p) => (p.adds.some((a) => a.client_ref === msg.client_ref)
            ? { ...p, adds: p.adds.filter((a) => a.client_ref !== msg.client_ref) } : p));
          // A Recording row still keyed by this ref means the /exec response
          // never landed — the data arriving proves the session exists, so
          // the server row (from the reload below) takes over.
          setPendingRecordings((prev) => prev.filter((r) => r.id !== msg.client_ref));
        }
        // A feed mutation landed (the delete a session_view "Remove" kicked off
        // echoes here) → clear any optimistic "Deleting…" tiles; the fresh feed
        // below no longer carries the removed rows (DESIGN.md: "the natural feed
        // refresh clears the pending"; app parity: pending is one-shot per push).
        setPendingOps((p) => ((p.deleting?.length || p.deleting_photos?.length || p.deleting_grids?.length)
          ? { ...p, deleting: [], deleting_photos: [], deleting_grids: [] } : p));
        reloadData();
        reloadSessions();
      } catch { /* skip */ }
    };
    es.addEventListener('widget_user_data_appended', handleDataAppended);
    es.addEventListener('widget_instance_updated', handleInstanceUpdated);
    es.addEventListener('widget_data_changed', handleDataChanged);
    return () => es.close();
  }, [isUser, instanceId, widgetId, reloadData, reloadSessions, reloadInstances]);

  // Skeleton-add age-out applies only before a real Session is bound. Bound
  // grids are durable and are cleared by `has_log` or explicit deletion.
  useEffect(() => {
    if (!pendingOps.adds.length) return undefined;
    const t = setInterval(() => {
      const cutoff = Date.now() - 5 * 60_000;
      setPendingOps((p) => (p.adds.some((a) => !a.session_id && a.started_at <= cutoff)
        ? { ...p, adds: p.adds.filter((a) => a.session_id || a.started_at > cutoff) } : p));
    }, 30_000);
    return () => clearInterval(t);
  }, [pendingOps.adds.length]);

  // ── HTML + payload loading ───────────────────────────────────────────────
  // Tag walk honors the studio's selected tag first, then falls back to the
  // other committed tag so a file missing from the selected version (e.g.
  // data_view.html only committed on dev) doesn't blank the phone.
  const tagOrder = useMemo(() => {
    const committed = ['dev', 'release'].filter((t) => widget?.tags?.[t]?.version_label);
    if (previewTag === 'scratch') return ['scratch', ...committed];
    return [previewTag, ...committed.filter((t) => t !== previewTag)];
  }, [previewTag, widget?.tags?.dev?.version_label, widget?.tags?.release?.version_label]);

  const loadFileText = useCallback(async (file) => {
    // Studio viewer: an exact version_label overrides the tag walk so the phone
    // face renders THAT version (not the dev head). No cross-version fallback —
    // a file missing from the version simply blanks, same as a tag miss.
    if (previewVersion) {
      try {
        return await widgetApi.readFile(widgetId, file, { version: previewVersion });
      } catch { return null; }
    }
    for (const tag of tagOrder) {
      try {
        if (tag === 'scratch') {
          if (!pinnedSessionId) continue;
          return await widgetApi.readScratchFile(widgetId, file, pinnedSessionId);
        }
        return await widgetApi.readFile(widgetId, file, { tag });
      } catch { /* try next tag */ }
    }
    return null;
  }, [previewVersion, tagOrder, widgetId, pinnedSessionId]);

  // Example payload — example_data.json through the same tag walk; reloads on
  // autosave (previewSha) so editing the fixture updates the phone live.
  // Loaded whenever the render is example-backed: Example mode AND the user
  // mode not-adopted Discovery preview (app parity: injectExampleData when
  // !_isUsed).
  useEffect(() => {
    if (!useExample) { setMockPayload(null); return undefined; }
    let alive = true;
    (async () => {
      const raw = await loadFileText('example_data.json');
      if (!alive) return;
      setMockPayload(parseExampleData(raw));
      setEntriesLoaded(true);
    })();
    return () => { alive = false; };
  }, [useExample, loadFileText, previewSha]);

  const mockEntries = useMemo(
    () => (Array.isArray(mockPayload?.entries) ? mockPayload.entries : []),
    [mockPayload],
  );
  const activeEntries = useExample ? mockEntries : entries;

  // ── Data-view host Session badge (app `_WidgetItemPage` parity) ──────────
  // Shown top-right of the item view when the focused entry's source session
  // is reachable: directly (source_session_id) or via its log_card_id (the
  // host resolves the owning session on tap). The data_view retargets the
  // anchor per carousel photo via the `session_badge_target` bridge message;
  // a deleted session hides the badge (the host refuses the jump anyway).
  const [badgeOverride, setBadgeOverride] = useState(null); // {sid, lcid, hidden} from session_badge_target
  useEffect(() => { setBadgeOverride(null); }, [itemEntryId]);
  const focusedEntry = useMemo(
    () => (activeEntries || []).find((e) => String(e.id) === String(itemEntryId)) || null,
    [activeEntries, itemEntryId],
  );
  const sessionBadge = useMemo(() => {
    // A standalone session-bound `open_page` page (session_view / data_view
    // alias — e.g. photo.html) gets the SAME top-right Session pill, jumping to
    // the source session carried in `page_args.session_id` (app
    // `_WidgetPagePage._SessionBadge`). Static — this host doesn't retarget the
    // badge per carousel photo, so `session_badge_target`/`badgeOverride` is
    // the item host's alone.
    if (view === 'page') {
      if (!isSessionBoundPageType(pageType)) return null;
      const raw = pageArgsRef.current?.session_id;
      const sid = typeof raw === 'string' ? raw.trim() : '';
      return sid ? { sid, lcid: null } : null;
    }
    if (view !== 'item') return null;
    if (badgeOverride) return badgeOverride.hidden ? null : badgeOverride;
    const e = focusedEntry;
    if (!e || e.session_deleted) return null;
    const sid = e.source_session_id || e.session_id || null;
    const lcid = e.log_card_id || e.payload?.log_card_id || null;
    if (!sid && !lcid) return null;
    return { sid, lcid };
  }, [view, pageType, badgeOverride, focusedEntry]);

  // The sandboxed iframe's origin is opaque — relative /api/media URLs can't
  // resolve there. Absolutize leading-slash media URLs before injection.
  const dataPayload = useMemo(() => {
    if (useExample) return mockPayload || {};
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    const absolutize = (v) =>
      (typeof v === 'string' && v.startsWith('/')) ? `${origin}${v}` : v;
    const fixEntry = (e) => {
      const p = e?.payload;
      if (!p || typeof p !== 'object') return e;
      const next = { ...p };
      if (typeof next.image_url === 'string') next.image_url = absolutize(next.image_url);
      if (Array.isArray(next.images)) next.images = next.images.map(absolutize);
      return { ...e, payload: next };
    };
    const projectedPending = {
      ...pendingOps,
      adds: mergePendingGridAdds(pendingOps.adds, sessions, { widgetId, instanceId }),
    };
    return {
      widget_id: widgetId,
      instance_id: instanceId,
      entries: (entries || []).map(fixEntry),
      settings: instance?.settings || {},
      // Loading contract: in-flight ops the widget may render as skeleton
      // tiles (adds) / dimmed entries (updates). Additive — old bundles
      // simply ignore it.
      pending: projectedPending,
    };
  }, [useExample, mockPayload, widgetId, instanceId, entries, instance?.settings, pendingOps, sessions]);

  const userPayload = useMemo(() => ({
    vi_user_id: api.getViUserId?.() || null,
    name: isUser ? (api.getUserDisplayName?.() || null) : 'Demo',
    appearance,
    // v1.1 host capabilities (mirrors the app): bundles hide their own bottom
    // CTA under the host bar and route sub-page back through the host.
    host_features: ['bottom_bar', 'subview_nav', 'direct_open_session'],
    instance_title: isUser
      ? (instance?.title || instance?.widget_title || widget?.title || null)
      : (widget?.title || null),
  }), [isUser, appearance, instance?.title, instance?.widget_title, widget?.title]);

  useEffect(() => {
    if (!widgetId) return undefined;
    let alive = true;
    (async () => {
      let html = `<!doctype html><html><body style="font-family:-apple-system,system-ui,sans-serif;color:#8E8E93;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;padding:24px;text-align:center;background:#F2F2F7;">Widget bundle not available yet — commit a version or edit a file.</body></html>`;
      const entryFile = view === 'item' ? 'data_view.html'
        : view === 'page' ? (pageFile || 'index.html')
        : 'index.html';
      const loaded = (await loadFileText(entryFile))
        ?? (entryFile !== 'index.html' ? await loadFileText('index.html') : null);
      if (loaded != null) html = loaded;

      let payloadObj = dataPayload;
      if (view === 'item' && itemEntryId) {
        payloadObj = { ...dataPayload, focus_entry_id: itemEntryId };
      } else if (view === 'page') {
        // dataPayload already carries `pending` (skeleton placeholders), so a
        // standalone page renders in-flight tiles for free; page_args is an
        // optional deterministic seed handed by open_page / a capture jump.
        payloadObj = { ...dataPayload, page_type: pageType, page_args: pageArgsRef.current || {} };
      }
      let payloadJson;
      try { payloadJson = JSON.stringify(payloadObj); } catch { payloadJson = '{}'; }
      // Hide any <vi_prefix>/<vi_suffix> instruction blocks the widget might
      // render (widget view + data view). Guarded: only deep-strip when a tag is
      // actually present, so the common (tag-free) feed is untouched.
      const rawPageArgs = payloadObj?.page_args;
      const visiblePayload = { ...payloadObj };
      delete visiblePayload.page_args;
      if (hasHiddenTags(JSON.stringify(visiblePayload))) {
        payloadObj = stripHiddenTagsDeep(visiblePayload);
      } else {
        payloadObj = visiblePayload;
      }
      if (rawPageArgs != null) payloadObj.page_args = rawPageArgs;
      try { payloadJson = JSON.stringify(payloadObj); } catch { /* keep prior json */ }
      let userJson;
      try { userJson = JSON.stringify(userPayload); } catch { userJson = '{}'; }
      // Reserve the host top band (iOS back chevron) like the app shell does.
      const bandStyle = '<style>:root{--neweyes-widget-safe-top:44px}body{padding-top:var(--neweyes-widget-safe-top,0)!important}</style>';
      const noSelectStyle = '<style>[data-bridge-action="open_item"],[data-item-id]{-webkit-user-select:none;user-select:none;-webkit-touch-callout:none;}[data-item-id] *{-webkit-user-select:none;user-select:none;}</style>';
      const inject = `${bandStyle}${noSelectStyle}<script>window.__WIDGET_DATA__=${payloadJson};window.__USER_DATA__=${userJson};window.__VI_E2E__=${e2eEnabled()};${BRIDGE_SCRIPT}${MOUSE_SWIPE_SHIM}</${'script'}>`;
      const headIdx = html.search(/<head[^>]*>/i);
      const headEnd = headIdx !== -1 ? html.indexOf('>', headIdx) + 1 : 0;
      const merged = headIdx !== -1
        ? html.slice(0, headEnd) + inject + html.slice(headEnd)
        : inject + html;
      // Mount-cascade churn guard: the first paint can run this effect
      // several times in quick succession (preview-tag flip, payload load,
      // SSE bump). Re-publishing identical bytes is wasted work, and —
      // worse — revoking the previous blob URL while the iframe is STILL
      // PARSING it aborts that load and Chrome leaves the frame blank
      // forever (the kick-the-src test confirmed this on beta). So:
      //   1. skip the publish entirely when the merged HTML is unchanged;
      //   2. revoke superseded URLs on a generous delay, never in the
      //      iframe's load window.
      if (merged === lastHtmlRef.current) return;
      // Lazy in-place update: when only the DATA changed (same widget file,
      // same view/focus, same user payload), forward the fresh payload into
      // the live iframe instead of rebuilding the blob — the exact contract
      // the app shell ships (`update_widget_data`, DESIGN.md §2.6): no
      // reload, no entrance animation, no scroll reset.
      const docKey = `${view}|${itemEntryId || ''}|${pageFile || ''}|${userJson}|${html}`;
      if (
        docKey === docKeyRef.current
        && iframeReadyRef.current
        && iframeElRef.current?.contentWindow
      ) {
        try {
          iframeElRef.current.contentWindow.postMessage(
            { source: 'NewEyesApp', type: 'update_widget_data', payload: payloadObj }, '*',
          );
          lastHtmlRef.current = merged;
          return;
        } catch { /* fall through to a full rebuild */ }
      }
      const blob = new Blob([merged], { type: 'text/html; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      if (!alive) { URL.revokeObjectURL(url); return; }
      lastHtmlRef.current = merged;
      docKeyRef.current = docKey;
      iframeReadyRef.current = false;
      const prev = blobUrlRef.current;
      if (prev) setTimeout(() => { try { URL.revokeObjectURL(prev); } catch { /* already revoked */ } }, 60_000);
      blobUrlRef.current = url;
      setPreviewUrl(url);
    })();
    return () => { alive = false; };
  }, [widgetId, dataPayload, userPayload, view, itemEntryId, pageFile, pageType, loadFileText, previewSha]);

  useEffect(() => () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
  }, []);

  // ── Session jump resolution (user mode) ──────────────────────────────────
  const jumpCtxRef = useRef({ entries: [], itemEntryId: null });
  useEffect(() => { jumpCtxRef.current = { entries: activeEntries, itemEntryId }; }, [activeEntries, itemEntryId]);
  const resolveJumpSid = useCallback(async (sid, lcid) => {
    if (sid) return sid;
    if (!lcid) return null;
    try { const s = await widgetInstances.sessionByLogCard(lcid); if (s) return s; } catch { /* ignore */ }
    const { entries: rows, itemEntryId: eid } = jumpCtxRef.current;
    const entry = eid ? (rows || []).find((e) => String(e.id) === String(eid)) : null;
    const baseLcid = entry?.log_card_id || entry?.payload?.log_card_id;
    if (baseLcid && baseLcid !== lcid) {
      try { return await widgetInstances.sessionByLogCard(baseLcid); } catch { /* ignore */ }
    }
    return null;
  }, []);

  const openSession = useCallback(async (s) => {
    if (!isUser) { mockGate(); return; }
    const lcid = s.log_card_id || s.logCardId;
    const sid = await resolveJumpSid(s.id || s.session_id, lcid);
    if (!sid) { showToast('This session was deleted'); return; }
    navigate(lcid ? `/playground/chat/${sid}?card=${encodeURIComponent(lcid)}` : `/playground/chat/${sid}`);
  }, [isUser, mockGate, navigate, resolveJumpSid, showToast]);

  const openMainSession = useCallback(async (prompt, { quickLog = false } = {}) => {
    if (!isUser) { mockGate(); return; }
    if (!instanceId) { handleUseThisWidget(); return; } // auto-adopt: the widget's own CTA IS the adoption
    if (mainBusy) return;
    setMainBusy(true);
    try {
      const res = await widgetInstances.mainSession(instanceId);
      const sid = res?.session_id;
      if (sid) {
        let ask = typeof prompt === 'string' ? prompt.trim() : '';
        // Phase 11 quick_log: a widget log button opens the main session AND
        // signals the agent to log this item immediately. Dispatch HERE with
        // entry=widget_quick_log + the typed ref (app parity — initialEntry)
        // so the server prefixes the quick-log directive and the fast runner
        // drops the prediction line; the old text-only #tag ask missed both
        // (beta 9ef243ef: log options rendered as prediction chips again).
        let dispatched = false;
        if (quickLog && ask) {
          ask = `${ask} #widget_instance_${instanceId}`;
          try {
            await api.dispatchExec({
              prompt: ask,
              sessionId: sid,
              entry: 'widget_quick_log',
              widgetRef: `#widget_instance_${instanceId}`,
            });
            dispatched = true;
          } catch (e) {
            console.warn('[widget-runtime] quick-log dispatch failed, falling back to ask param:', e);
          }
        }
        // Studio surface: open the main session view IN PLACE (cover + chat)
        // instead of leaving for /playground/chat — keeps the widget context.
        // A dispatched quick-log opens the LIVE session with no ask (the
        // in-flight turn streams in via WAL/SSE replay).
        if (onOpenMainSession) {
          onOpenMainSession({ sessionId: sid, ask: dispatched ? '' : ask, widgetId, instanceId, tag: previewTag });
        } else {
          navigate(!dispatched && ask ? `/playground/chat/${sid}?ask=${encodeURIComponent(ask)}` : `/playground/chat/${sid}`);
        }
      }
    } catch (e) {
      console.warn('[widget-runtime] open main session failed:', e);
    } finally {
      setMainBusy(false);
    }
  }, [isUser, mockGate, instanceId, mainBusy, navigate, showToast, onOpenMainSession, widgetId, previewTag]);

  // Web "Save to Album" = download the photo (CORS failure → open in new tab).
  const saveImage = useCallback(async (url) => {
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      let base = 'photo';
      try { base = new URL(url, window.location.href).pathname.split('/').pop() || 'photo'; } catch { /* keep default */ }
      a.download = base.includes('.') ? base : `${base}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  }, []);

  // Host-owned file download (docx/pdf/xlsx/… — the widget "download" bridge).
  // Same blob-fetch as saveImage but honors the real filename/extension; CORS
  // failure falls back to opening the (already host-resolved) URL in a new tab.
  const downloadFile = useCallback(async (url, filename) => {
    // The download is host-performed and otherwise SILENT (the file just lands in
    // the browser's downloads) — in the phone-frame that reads as "nothing
    // happened". A toast is the only in-widget signal, so always show one.
    showToast('Downloading…');
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      let base = (filename || '').trim();
      if (!base) { try { base = new URL(url, window.location.href).pathname.split('/').pop() || 'file'; } catch { base = 'file'; } }
      a.download = base;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      showToast('Saved to your device');
    } catch {
      // CORS/network failure → open the (host-resolved) URL directly as a fallback.
      showToast('Opening the file…');
      window.open(url, '_blank', 'noopener');
    }
  }, [showToast]);

  const deleteSession = useCallback(async (s) => {
    if (!isUser) { mockGate(); return; }
    const sid = s.id || s.session_id;
    if (!sid) return;
    const gridId = s.grid_id || s.client_ref || sid;
    setPendingOps((p) => ({
      ...p,
      adds: p.adds.filter((add) => add.session_id !== sid && add.client_ref !== gridId),
    }));
    setSessions((prev) => {
      const next = prev.filter((row) => (row.id || row.session_id) !== sid);
      cacheSet(`sessions:${instanceId}`, next);
      return next;
    });
    try {
      await api.deleteSession(sid);
    } catch (e) {
      console.warn('[widget-runtime] session delete failed; refetching:', e);
      reloadSessions();
    }
  }, [isUser, mockGate, instanceId, reloadSessions]);

  const entryGridId = useCallback((entry) => {
    const sid = String(entry?.source_session_id || '').trim();
    if (sid) return sid;
    const p = entry?.payload || {};
    const photo = String(p.photo_url || p.image_url || '').trim();
    return photo ? photo.split('?')[0] : '';
  }, []);

  const deleteGrid = useCallback(async (gridId) => {
    if (!isUser) { setItemMenu(null); mockGate(); return; }
    const target = String(gridId || '').trim();
    if (!target || itemMenuBusy) return;
    setItemMenuBusy(true);
    setPendingOps((p) => ({
      ...p,
      deleting_grids: [...new Set([...(p.deleting_grids || []), target])],
    }));
    try {
      const ids = (entries || []).filter((entry) => entryGridId(entry) === target).map((entry) => entry.id).filter(Boolean);
      await Promise.all(ids.map((entryId) => widgetInstances.deleteData(instanceId, entryId)));
      setEntries((prev) => {
        const next = (prev || []).filter((entry) => entryGridId(entry) !== target);
        cacheSet(`entries:${instanceId}`, next);
        return next;
      });
      await reloadData();
      await reloadSessions();
    } catch (e) {
      console.warn('[widget-runtime] grid delete failed:', e);
      setPendingOps((p) => ({
        ...p,
        deleting_grids: (p.deleting_grids || []).filter((id) => id !== target),
      }));
      reloadData();
    } finally {
      setItemMenuBusy(false);
      setItemMenu(null);
    }
  }, [isUser, mockGate, itemMenuBusy, entries, entryGridId, instanceId, reloadData, reloadSessions]);

  const deletePendingGrid = useCallback(async (sessionId, gridId) => {
    if (!isUser) { setItemMenu(null); mockGate(); return; }
    const sid = String(sessionId || '').trim();
    const target = String(gridId || sid).trim();
    if (!sid || !target || itemMenuBusy) return;
    setItemMenuBusy(true);
    setPendingOps((p) => ({
      ...p,
      deleting_grids: [...new Set([...(p.deleting_grids || []), target])],
    }));
    try {
      await api.deleteSession(sid);
      setSessions((prev) => {
        const next = (prev || []).filter((row) => String(row.id || row.session_id) !== sid);
        cacheSet(`sessions:${instanceId}`, next);
        return next;
      });
      setPendingOps((p) => ({
        ...p,
        adds: (p.adds || []).filter((add) => String(add.session_id || '') !== sid && String(add.client_ref || '') !== target),
        deleting_grids: (p.deleting_grids || []).filter((id) => id !== target),
      }));
      await reloadData();
      await reloadSessions();
    } catch (e) {
      console.warn('[widget-runtime] pending grid delete failed:', e);
      setPendingOps((p) => ({
        ...p,
        deleting_grids: (p.deleting_grids || []).filter((id) => id !== target),
      }));
      reloadSessions();
    } finally {
      setItemMenuBusy(false);
      setItemMenu(null);
    }
  }, [isUser, mockGate, itemMenuBusy, instanceId, reloadData, reloadSessions]);

  const deleteItem = useCallback(async (entryId) => {
    if (!isUser) { setItemMenu(null); mockGate(); return; }
    if (!entryId || itemMenuBusy) return;
    setItemMenuBusy(true);
    // Seed the per-tile "Deleting…" marker BEFORE the request so the row —
    // still in `entries` for the duration of the round-trip — renders as
    // deleting instead of only sitting under the full-screen overlay. The
    // optimistic filter below (and the SSE echo) drops it right after.
    setPendingOps((p) => ({ ...p, deleting_grids: [...(p.deleting_grids || []), String(entryId)] }));
    try {
      await widgetInstances.deleteData(instanceId, entryId);
      setEntries((prev) => {
        const next = (prev || []).filter((e) => e.id !== entryId);
        cacheSet(`entries:${instanceId}`, next);
        return next;
      });
      if (view === 'item' && itemEntryId === entryId) closeItemView();
      reloadData();
    } catch (e) {
      console.warn('[widget-runtime] item delete failed:', e);
      // The row survives a failed delete — drop its marker so it doesn't stay
      // stuck on "Deleting…" (no SSE echo arrives for a delete that failed).
      setPendingOps((p) => ({
        ...p,
        deleting_grids: (p.deleting_grids || []).filter((x) => x !== String(entryId)),
      }));
      reloadData();
    } finally {
      setItemMenuBusy(false);
      setItemMenu(null);
    }
  }, [isUser, mockGate, instanceId, itemMenuBusy, view, itemEntryId, reloadData, closeItemView]);

  // "⋯ → Delete this Widget" — un-adopts THIS instance (the app's
  // `_confirmDeleteWidget` → deleteWidgetInstance). The backend FK is
  // ondelete=CASCADE, so the user's logged data for the instance is
  // permanently removed. The app pops back to the catalog; in the studio we
  // stay on the page and the runtime reverts IN PLACE to the not-adopted
  // Discovery state (example content + "Use this Widget").
  const handleDeleteInstance = useCallback(async () => {
    if (!instanceId || instanceDeleteBusy) return;
    setInstanceDeleteBusy(true);
    try {
      await widgetInstances.remove(instanceId);
      try {
        localStorage.removeItem(`${CACHE_NS}:entries:${instanceId}`);
        localStorage.removeItem(`${CACHE_NS}:sessions:${instanceId}`);
      } catch { /* noop */ }
      const rest = (instances || []).filter((i) => i.instance_id !== instanceId);
      setEntries([]);
      setSessions([]);
      setPendingRecordings([]);
      setInstances(rest);
      // Template with other instances → bind the next one (still adopted);
      // last/only instance → not-adopted state (example + CTA).
      setInstanceId(rest[0]?.instance_id || null);
      setConfirmInstanceDelete(false);
      setView('widget');
      // Strip stale deep-link params so the URL reflects the new state.
      const params = new URLSearchParams(location.search);
      params.delete('instance');
      params.delete('item');
      const q = params.toString();
      navigate(q ? `${location.pathname}?${q}` : location.pathname, { replace: true });
    } catch (e) {
      console.warn('[widget-runtime] delete instance failed:', e);
      showToast('Delete failed — try again');
    } finally {
      setInstanceDeleteBusy(false);
    }
  }, [instanceId, instanceDeleteBusy, instances, navigate, location.pathname, location.search, showToast]);

  // Canon Common-UX #12: Send does NOT jump to the chat session — the sheet
  // just closes and the "Recording…" row (onDispatchStarted) carries the
  // progress in the session list; the 8s poll + SSE reconcile it.
  const onCaptureClose = useCallback((res) => {
    // Resolve the widget's optimistic in-slot "processing" tile (Depop quick
    // add): dispatched → keep it until the cutout lands; canceled → clear now.
    try {
      iframeElRef.current?.contentWindow?.postMessage(
        { source: 'NewEyesApp', type: 'host_capture', payload: { dispatched: !!(res && res.dispatched) } }, '*');
    } catch { /* ignore */ }
    setCaptureOpen(false);
    setCapturePrefill(null);
    setCaptureSessionId(null);
    setCaptureQuick(false);
    setCaptureImages([]);
  }, []);

  // Reply to a request-style widget bridge message without rebuilding the
  // sandboxed iframe. `pick_media` uses this to return authenticated uploads
  // while keeping the widget page and its role-slot state mounted.
  const replyToWidgetRequest = useCallback((requestId, payload) => {
    if (!requestId) return;
    try {
      iframeElRef.current?.contentWindow?.postMessage(
        { source: 'NewEyesApp', type: 'response', requestId, payload: payload || {} }, '*',
      );
    } catch { /* iframe may have closed while the native picker was open */ }
  }, []);

  // A rejected host request can still have created a backend Session (notably
  // the no-credit path). Reconcile immediately and keep the transient grid for
  // up to five minutes; a real Session binds it durably, while a failure before
  // dispatch naturally expires.
  const onDispatchFailed = useCallback((_tempId, error) => {
    reloadSessions();
    const brief = String(error?.message || error || '').slice(0, 120);
    showToast(`Log failed — ${brief}`);
  }, [reloadSessions, showToast]);

  // ── Bridge message handler ───────────────────────────────────────────────
  useEffect(() => {
    const onMsg = (evt) => {
      const data = evt?.data || {};
      if (data.source !== 'NewEyesWidget') return;
      if (iframeElRef.current && evt.source !== iframeElRef.current.contentWindow) return;
      if (data.type === 'widget_subview') {
        setWidgetSubview(data.payload?.active === true);
        return;
      }
      if (data.type === 'widget_nav_action') {
        // Widgets-refine nav: bundle declares its bar middle action
        // ('search' | 'edit_goal' | 'gallery'); tap posts host_nav_action.
        const a = typeof data.payload?.action === 'string' ? data.payload.action.trim() : '';
        setNavAction(a || null);
        return;
      }
      if (data.type === 'open_capture') {
        if (!isUser) { mockGate(); return; }
        if (!instanceId) { handleUseThisWidget(); return; } // auto-adopt: the widget's own CTA IS the adoption
        const prefill = typeof data.payload?.prompt === 'string' ? data.payload.prompt.trim() : '';
        const contSid = typeof data.payload?.session_id === 'string' ? data.payload.session_id.trim() : '';
        const tgtPage = typeof data.payload?.target_page === 'string' ? data.payload.target_page.trim() : '';
        const rawImgs = data.payload?.images;
        const imgs = Array.isArray(rawImgs)
          ? rawImgs.filter((s) => typeof s === 'string' && s.trim()).map((s) => s.trim())
          : (typeof rawImgs === 'string' && rawImgs.trim() ? [rawImgs.trim()] : []);
        captureTargetPageRef.current = tgtPage || null;
        setCapturePrefill(prefill || null);
        setCaptureSessionId(contSid || null);
        setCaptureQuick(data.payload?.quick === true);
        setCaptureImages(imgs);
        // Widgets-refine PRD (v0.8.2, change 04): add CTAs are quick
        // (composer-less) by default; a bundle opts back in with quick:false.
        setCaptureQuick(data.payload?.quick !== false);
        setCaptureOpen(true);
        return;
      }
      if (data.type === 'pick_media') {
        const requestId = typeof data.requestId === 'string' ? data.requestId.trim() : '';
        if (!requestId) return;
        if (!isUser) {
          mockGate();
          replyToWidgetRequest(requestId, { images: [], canceled: true, error: 'user_data_required' });
          return;
        }
        if (!instanceId) {
          handleUseThisWidget();
          replyToWidgetRequest(requestId, { images: [], canceled: true, error: 'adopt_required' });
          return;
        }
        if (pickMediaRequestRef.current) {
          replyToWidgetRequest(requestId, { images: [], canceled: true, error: 'picker_busy' });
          return;
        }
        const requested = Number.parseInt(data.payload?.max_count, 10);
        const maxCount = Math.max(1, Math.min(4, Number.isFinite(requested) ? requested : 1));
        pickMediaRequestRef.current = {
          requestId,
          maxCount,
          roleId: typeof data.payload?.role_id === 'string' ? data.payload.role_id : null,
        };
        const input = pickMediaFileRef.current;
        if (!input) {
          pickMediaRequestRef.current = null;
          replyToWidgetRequest(requestId, { images: [], canceled: true, error: 'unsupported' });
          return;
        }
        input.multiple = maxCount > 1;
        input.click();
        return;
      }
      if (data.type === 'open_session') {
        if (!isUser) { mockGate(); return; }
        const lcid = data.payload?.log_card_id;
        const ask = typeof data.payload?.prompt === 'string' ? data.payload.prompt.trim() : '';
        (async () => {
          const sid = await resolveJumpSid(data.payload?.session_id, lcid);
          if (!sid) { showToast('This session was deleted'); return; }
          const params = new URLSearchParams();
          if (lcid) params.set('card', lcid);
          if (ask) params.set('ask', ask);
          const q = params.toString();
          navigate(q ? `/playground/chat/${sid}?${q}` : `/playground/chat/${sid}`);
        })();
        return;
      }
      if (data.type === 'open_item') {
        // Canonical bridge payload is {item_id} (neweyes_bridge contract —
        // widget-authored postMessage, e.g. Personal Studio openBoard sends
        // 'board:<name>'); the host shell script posts {entry_id}. Accept
        // both, app order (item_id first) — entry_id-only silently dropped
        // every widget-authored open_item on web (boards grid dead tap).
        const eid = data.payload?.item_id || data.payload?.entry_id;
        if (eid) openItemView(eid);
        return;
      }
      if (data.type === 'open_page') {
        // Host-template scheme: load an arbitrary bundle file with a chosen
        // host overlay type (default 'index' — no session badge, own back).
        const file = typeof data.payload?.file === 'string' ? data.payload.file.trim() : '';
        if (!file) return;
        // 'session_view' (canonical) and its 'data_view' alias are both session-
        // bound; any other value is the collection surface ('index').
        const type = isSessionBoundPageType(data.payload?.type) ? data.payload.type : 'index';
        const args = (data.payload?.args && typeof data.payload.args === 'object') ? data.payload.args : null;
        openPageView(file, type, args);
        return;
      }
      if (data.type === 'close_page') {
        // A standalone page finished and wants to pop back to its opener (app's
        // webview self-pop + `onClosePage`). The optional confirmation toast is
        // shown BEFORE we navigate away. A session-scoped Remove from a
        // session_view page (photo.html) carries `{deleting_session,
        // deleting_photo}` and NO toast — seed the "Deleting…" pending on the
        // opener grid (index.html) so the tile the user lands back on shows it
        // until the feed refresh drops the row (DESIGN.md pending.deleting; app
        // `pushDeletingPending`). Fire on EITHER field: older captures have an
        // empty source_session_id, so the photo url is the load-bearing fallback.
        const t = typeof data.payload?.toast === 'string' ? data.payload.toast.trim() : '';
        if (t) showToast(t);
        const delSid = typeof data.payload?.deleting_session === 'string' ? data.payload.deleting_session.trim() : '';
        const delPhoto = typeof data.payload?.deleting_photo === 'string' ? data.payload.deleting_photo.trim() : '';
        if (delSid || delPhoto) {
          setPendingOps((p) => {
            const deleting = p.deleting || [];
            const deletingPhotos = p.deleting_photos || [];
            return {
              ...p,
              deleting: (delSid && !deleting.includes(delSid)) ? [...deleting, delSid] : deleting,
              deleting_photos: (delPhoto && !deletingPhotos.includes(delPhoto)) ? [...deletingPhotos, delPhoto] : deletingPhotos,
            };
          });
        }
        // Pop this page back to the opener (index.html at view='widget'), the
        // web analog of the app webview self-popping its own route.
        if (view === 'page') closePageView();
        return;
      }
      if (data.type === 'attach_item_image') {
        if (!isUser) { mockGate(); return; }
        const eid = data.payload?.entry_id || data.payload?.item_id || itemEntryId;
        if (eid) { attachEntryIdRef.current = eid; attachFileRef.current?.click(); }
        return;
      }
      if (data.type === 'item_options') {
        const eid = data.payload?.entry_id || data.payload?.item_id;
        const gridId = data.payload?.grid_id;
        const pendingSessionId = data.payload?.pending_session_id;
        if (eid || gridId) setItemMenu({ entryId: eid || null, gridId: gridId || null, pendingSessionId: pendingSessionId || null });
        return;
      }
      if (data.type === 'session_badge_target') {
        // Data view → host badge sync: the selected carousel photo's session
        // anchor (appended photos carry their OWN widget_log card). Empty
        // payload (or session_deleted) hides the badge for this selection.
        const sid = typeof data.payload?.session_id === 'string' ? data.payload.session_id.trim() : '';
        const lcid = typeof data.payload?.log_card_id === 'string' ? data.payload.log_card_id.trim() : '';
        const hidden = data.payload?.session_deleted === true || (!sid && !lcid);
        setBadgeOverride({ sid: sid || null, lcid: lcid || null, hidden });
        return;
      }
      if (data.type === 'open_main_session') {
        openMainSession(data.payload?.prompt, { quickLog: data.payload?.quick_log === true });
        return;
      }
      if (data.type === 'log_data') {
        if (!isUser) { mockGate(); return; }
        const kind = typeof data.payload?.kind === 'string' ? data.payload.kind.trim() : '';
        const rowPayload = (data.payload?.payload && typeof data.payload.payload === 'object') ? data.payload.payload : null;
        if (!instanceId || !kind || !rowPayload) return;
        (async () => {
          try {
            await widgetInstances.appendData(instanceId, {
              kind,
              payload: rowPayload,
              log_card_id: data.payload?.log_card_id || null,
            });
            reloadData();
          } catch (e) {
            console.warn('[widget-runtime] log_data append failed:', e);
            showToast("Couldn't save that");
          }
        })();
        return;
      }
      if (data.type === 'item_action') {
        const act = data.payload?.action;
        if (act === 'save_image') {
          const url = data.payload?.image_url;
          if (url) saveImage(url);
          return;
        }
        if (act === 'download') {
          // Host-owned file download (the widget download bridge). The URL is
          // already host-resolved from a media:// ref by the /data feed.
          const url = data.payload?.file_url || data.payload?.image_url;
          if (url) downloadFile(url, data.payload?.filename);
          return;
        }
        if (act !== 'delete') return;
        if (!isUser) { mockGate(); return; }
        const eid = data.payload?.entry_id;
        const lcid = data.payload?.log_card_id;
        const sid = data.payload?.session_id;
        const idxRaw = data.payload?.image_index;
        const idx = (idxRaw === undefined || idxRaw === null || idxRaw === '') ? null : parseInt(idxRaw, 10);
        (async () => {
          try {
            if (eid && Number.isInteger(idx) && idx >= 0) {
              // Per-photo (canon Common-UX #7): drop just that image; the
              // whole entry goes only when it was the last one. The entry is
              // marked pending (the widget dims it) until the fresh feed lands.
              const entry = (entries || []).find((x) => String(x.id) === String(eid));
              const imgs = Array.isArray(entry?.payload?.images) ? entry.payload.images
                : (entry?.payload?.image_url ? [entry.payload.image_url] : []);
              if (imgs.length > 1 && idx < imgs.length) {
                addPendingUpdate(eid);
                try {
                  await widgetInstances.setImages(instanceId, eid, imgs.filter((_, i) => i !== idx));
                  await reloadData();
                } finally {
                  clearPendingUpdate(eid);
                }
              } else {
                deleteItem(eid);
              }
            } else if (eid) {
              deleteItem(eid);
            } else if (lcid) {
              await widgetInstances.cancelData(instanceId, lcid);
              reloadData(); reloadSessions();
            } else if (sid) {
              deleteSession({ id: sid });
            }
          } catch (e2) {
            console.warn('[widget-runtime] item_action delete failed:', e2);
            showToast("Couldn't delete that");
            reloadData();
          }
        })();
        return;
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [isUser, mockGate, navigate, instanceId, entries, itemEntryId, view, deleteItem, deleteSession, openItemView, openPageView, closePageView, openMainSession, reloadData, reloadSessions, resolveJumpSid, saveImage, downloadFile, showToast, addPendingUpdate, clearPendingUpdate, handleUseThisWidget, replyToWidgetRequest]);

  // Hidden picker for the data_view "+" attach (user mode). Uploads every
  // selected file, appends deterministically, then continues the source
  // session with the short append-contract message.
  const onAttachFilePicked = useCallback(async (e) => {
    const files = Array.from(e?.target?.files || []);
    const entryId = attachEntryIdRef.current;
    if (e?.target) e.target.value = '';
    if (!files.length || !entryId || !isUser) return;
    addPendingUpdate(entryId); // dim the entry while the upload+attach runs
    try {
      const urls = [];
      for (const file of files) {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = () => reject(r.error);
          r.readAsDataURL(file);
        });
        const url = await api.uploadDataUrl(dataUrl, { useProxyUrl: true });
        if (url) urls.push(url);
      }
      if (!urls.length) { clearPendingUpdate(entryId); return; }
      await widgetInstances.attachImages(instanceId, entryId, urls);
      await reloadData();
      clearPendingUpdate(entryId); // photo visible — agent enrichment lands later via SSE
      try {
        const entry = (entries || []).find((en) => String(en?.id) === String(entryId));
        const p = entry?.payload || {};
        const rawName = p.name || p.title || p.common_name || p.model || p.species || '';
        const lcid = entry?.log_card_id || null;
        let sid = entry?.source_session_id || entry?.session_id || null;
        if (!sid && lcid) {
          try { sid = await widgetInstances.sessionByLogCard(lcid); } catch { /* fall through */ }
        }
        const n = urls.length === 1 ? 'one more' : `${urls.length} more`;
        const noun = urls.length === 1 ? 'photo' : 'photos';
        const name = rawName ? `"${String(rawName).trim()}"` : 'this item';
        const tags = `#widget_instance_${instanceId}${lcid ? ` #widget_append_${lcid}` : ''}`;
        await api.dispatchExec({
          prompt: `Added ${n} ${noun} of ${name} ${tags}`,
          mediaUrls: urls,
          sessionId: sid || undefined,
          context: { widget_use_contract: { widget_id: widgetId || undefined, widget_instance_id: instanceId } },
        });
      } catch (err) {
        console.warn('[widget-runtime] append session continue failed:', err);
      }
    } catch (err) {
      console.warn('[widget-runtime] attach image failed:', err);
      clearPendingUpdate(entryId);
    }
  }, [isUser, instanceId, widgetId, entries, reloadData, addPendingUpdate, clearPendingUpdate]);

  // Upload role photos selected from a widget subpage, then return only safe
  // hosted URLs to that mounted page. No session is created at this stage.
  const finishPickMedia = useCallback((payload) => {
    const pending = pickMediaRequestRef.current;
    pickMediaRequestRef.current = null;
    if (pending?.requestId) {
      replyToWidgetRequest(pending.requestId, {
        role_id: pending.roleId || undefined,
        ...payload,
      });
    }
  }, [replyToWidgetRequest]);

  const onPickMediaFilePicked = useCallback(async (e) => {
    const pending = pickMediaRequestRef.current;
    const files = Array.from(e?.target?.files || []).slice(0, pending?.maxCount || 1);
    if (e?.target) e.target.value = '';
    if (!pending) return;
    if (!files.length) {
      finishPickMedia({ images: [], canceled: true });
      return;
    }
    try {
      const images = [];
      for (const file of files) {
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(file);
        });
        // The result is rendered inside a sandboxed opaque-origin iframe, so
        // return the upload's directly loadable public URL rather than an
        // auth-cookie-dependent `/api/media` proxy URL.
        const url = await api.uploadDataUrl(dataUrl, { format: 'signed', skipCache: true });
        if (url) images.push({ url, media_type: 'image', name: file.name });
      }
      finishPickMedia({ images, canceled: images.length === 0 });
    } catch (error) {
      console.warn('[widget-runtime] pick_media upload failed:', error);
      showToast('Upload failed — try again');
      finishPickMedia({ images: [], canceled: true, error: 'upload_failed' });
    }
  }, [finishPickMedia, showToast]);

  const onPickMediaCanceled = useCallback(() => {
    if (pickMediaRequestRef.current) finishPickMedia({ images: [], canceled: true });
  }, [finishPickMedia]);

  // Designer-authored capture-overlay copy (widget.json.capture via summary).
  const skillHint = useMemo(() => {
    const raw = instance?.capture || null;
    if (!raw || typeof raw !== 'object') return null;
    return {
      title: raw.title || null,
      heroLabel: raw.hero_label || null,
      heroSub: raw.hero_sub || null,
      aiNote: raw.ai_note || null,
      notePlaceholder: raw.note_placeholder || null,
    };
  }, [instance?.capture]);

  // ── Render ───────────────────────────────────────────────────────────────
  // Not-adopted user mode renders the example-data preview with the
  // "Use this Widget" CTA overlaid (the app's Discovery view) — never an
  // auto-instantiate, never a placeholder screen.
  const isTemplate = widget?.widget_type === 'template';
  // No "Use this Widget" button — standard widgets adopt through their own
  // CTA (auto-adopt above). Only template collections keep the capsule (naming).
  const showCta = useExample && view === 'widget' && instances != null && widget?.widget_type === 'template';
  const loading = (isUser && instances == null) || !entriesLoaded || !previewUrl;

  return (
    <div style={{
      width: '100%', height: '100%', overflow: 'hidden',
      background: T.bg, position: 'relative', fontFamily: FONT_UI,
    }}>
      {(isOverlayView || view === 'widget') ? (
        loading ? (
          <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.textMut, fontSize: 13, background: T.bg,
          }}>Loading widget…</div>
        ) : (
          <iframe
            ref={iframeElRef}
            src={previewUrl}
            onLoad={() => { iframeReadyRef.current = true; }}
            title={`widget-runtime-${widgetId}`}
            sandbox="allow-scripts"
            style={{ width: '100%', height: '100%', border: 0, background: T.bg }}
          />
        )
      ) : (
        isUser ? (
          <SessionList
            sessions={mergedSessions}
            widgetTitle={instance?.title || widget?.title || 'Widget'}
            onOpen={openSession}
            onDelete={deleteSession}
          />
        ) : (
          <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 32, color: T.textMut, fontSize: 13, textAlign: 'center',
          }}>
            Example data — switch to User Data to see your real sessions.
          </div>
        )
      )}

      {/* Pending quick-log attention row (host layer, every widget): unacted
          pending Log cards persist as a tappable strip until logged/canceled.
          Tap = jump to the owning session anchored at the pending card. */}
      {isUser && view === 'widget' && !loading && pendingLogs.length > 0 && (
        <button
          type="button"
          onClick={() => openSession({ id: pendingLogs[0].session_id, log_card_id: pendingLogs[0].log_card_id })}
          style={{
            position: 'absolute', top: 52, left: 12, right: 12, zIndex: 39,
            display: 'flex', alignItems: 'center', gap: 8,
            background: '#FFF8E7', border: '1px solid #F0C36D', borderRadius: 12,
            padding: '10px 12px', cursor: 'pointer', textAlign: 'left',
            boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
          }}
        >
          <span style={{ fontSize: 16 }}>⏳</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: '#6b5417', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {(pendingLogs[0].payload?.title || pendingLogs[0].kind || 'One entry')}
            {' — tap to finish logging'}
            {pendingLogs.length > 1 ? ` (+${pendingLogs.length - 1} more)` : ''}
          </span>
          <span style={{ fontSize: 11, color: '#a8842a', fontWeight: 700, flexShrink: 0 }}>PENDING</span>
        </button>
      )}

      {/* Host iOS back chevron in the reserved top band — app-like back on
          EVERY view: the item detail pops back to the album, the sessions
          list pops back to the widget, and the widget view goes back to
          wherever the user came from (catalog, log-card jump, a session…).
          The widget itself never draws a back button (STYLE.md: the host
          owns the iOS chrome). */}
      <GlassCircle
        onClick={() => {
          if (view === 'page') closePageView();
          else if (view === 'item') closeItemView();
          else if (view === 'sessions') setView('widget');
          else if (widgetSubview) {
            // Widget owns this back: it navigates its sub-page home.
            try {
              iframeElRef.current?.contentWindow?.postMessage(
                { source: 'NewEyesApp', type: 'host_back' }, '*',
              );
            } catch { /* ignore */ }
          } else navigate(-1);
        }}
        label="Back"
        style={{ position: 'absolute', top: 9, left: 12, zIndex: 40 }}
      >
        <ChevronLeft size={18} strokeWidth={2.4} />
      </GlassCircle>

      {/* Host-drawn Session badge — top-right of the DATA VIEW (per-item
          `data_view.html`) when the focused entry's source session is reachable,
          AND of a session-bound `open_page` page (session_view / data_view alias
          — e.g. photo.html) via its `page_args.session_id` (app `_SessionBadge`).
          Tap = jump to the owning chat session anchored at the selected photo's
          log card; the data_view retargets it per carousel photo via
          `session_badge_target`. */}
      {isUser && isOverlayView && sessionBadge && (
        <button
          type="button"
          onClick={() => openSession({ id: sessionBadge.sid, log_card_id: sessionBadge.lcid })}
          style={{
            position: 'absolute', top: 9, right: 12, zIndex: 40,
            display: 'flex', alignItems: 'center', gap: 6,
            height: 36, padding: '0 14px', borderRadius: 100, border: 'none',
            background: 'rgba(0,0,0,0.42)', color: '#FFFFFF',
            fontSize: 12, fontWeight: 700, fontFamily: FONT_UI,
            cursor: 'pointer',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <MessageCircle size={14} strokeWidth={2.2} />
          Session
        </button>
      )}

      {/* Click-away backdrop for the "⋯" menu — sits BELOW the top-right
          chrome so the menu itself stays clickable. */}
      {overflowOpen && (
        <div
          onClick={() => setOverflowOpen(false)}
          style={{ position: 'absolute', inset: 0, zIndex: 39 }}
        />
      )}

      {/* Top-right floating chrome (app `_CatalogWidgetViewPage` rules):
          the "⋯" overflow shows ONLY on the widget content view when an
          instance is adopted (there's something to un-adopt); the template
          instance picker (web convenience) sits to its left. A standalone
          page (view==='page') is a pushed sub-page — no top-level chrome. */}
      {!isOverlayView && (
        <div style={{
          position: 'absolute', top: 9, right: 12, zIndex: 40,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {isUser && (instances || []).length > 1 && (
            <select
              value={instanceId || ''}
              onChange={(e) => setInstanceId(e.target.value)}
              title="Which of your instances to use"
              style={{
                maxWidth: 180, padding: '5px 8px', borderRadius: 8,
                border: `0.7px solid ${T.cell}`, background: T.card, color: T.text,
                fontSize: 11, fontWeight: 600, fontFamily: FONT_UI, cursor: 'pointer',
              }}
            >
              {(instances || []).map((i) => (
                <option key={i.instance_id} value={i.instance_id}>
                  {i.title || i.widget_title || i.instance_id.slice(0, 8)}
                </option>
              ))}
            </select>
          )}
          {adopted && view === 'widget' && (
            <div style={{ position: 'relative' }}>
              <GlassCircle onClick={() => setOverflowOpen((v) => !v)} label="More">
                <MoreHorizontal size={18} strokeWidth={2.2} />
              </GlassCircle>
              {overflowOpen && (
                <div style={{
                  position: 'absolute', top: 42, right: 0, minWidth: 180,
                  background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)', borderRadius: 12,
                  boxShadow: '0 10px 36px rgba(0,0,0,0.22)', padding: 4, zIndex: 50,
                }}>
                  <button
                    type="button"
                    onClick={() => { setOverflowOpen(false); setConfirmInstanceDelete(true); }}
                    style={{
                      width: '100%', textAlign: 'left', display: 'flex',
                      alignItems: 'center', gap: 8,
                      background: 'transparent', border: 'none', borderRadius: 8,
                      padding: '10px 12px', fontSize: 14, fontWeight: 600,
                      color: '#E5484D', cursor: 'pointer', fontFamily: FONT_UI,
                    }}
                  >
                    <Trash2 size={16} strokeWidth={2.2} />
                    Delete this Widget
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Widgets-refine nav (Figma 26184:9576) — ONE compact centered icon
          pill: Home · bundle-declared action (search / edit_goal / gallery) ·
          ＋. Histories and Modify have no slots. Shown ONLY once adopted. */}
      {adopted && !isOverlayView && !(widgetSubview && view === 'widget')
        && !(entriesLoaded && entries.length === 0 && view === 'widget') && (
        <div
          style={{
            position: 'absolute', left: 16, right: 16, bottom: 18,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            pointerEvents: 'none', zIndex: 30,
          }}
        >
          <div
            style={{
              pointerEvents: 'auto',
              background: 'rgba(255,255,255,0.94)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: 100,
              display: 'flex', alignItems: 'stretch',
              padding: 5, height: 52, boxSizing: 'border-box',
              boxShadow: '0 4px 18px rgba(0,0,0,0.18)',
              fontFamily: FONT_UI,
            }}
          >
            {[
              {
                key: 'widget', label: 'Home', Icon: LayoutGrid,
                selected: view === 'widget',
                onClick: () => setView('widget'),
              },
              ...(navAction ? [{
                key: 'navAction',
                label: navAction === 'edit_goal' ? 'Edit' : navAction === 'gallery' ? 'Gallery' : 'Search',
                Icon: navAction === 'edit_goal' ? Pencil : navAction === 'gallery' ? UserRound : Search,
                selected: false,
                onClick: () => {
                  // Hand the tap to the bundle — it opens its own UI.
                  if (view !== 'widget') setView('widget');
                  iframeElRef.current?.contentWindow?.postMessage(
                    { source: 'NewEyesApp', type: 'host_nav_action' }, '*',
                  );
                },
              }] : []),
              {
                key: 'add', label: 'Add', Icon: Plus,
                selected: false,
                onClick: () => {
                  // ＋ — camera-first, composer-less (PRD v0.8.2 change 04).
                  if (!isUser) { mockGate(); return; }
                  if (!instanceId) { handleUseThisWidget(); return; } // auto-adopt: the widget's own CTA IS the adoption
                  setCapturePrefill(null); setCaptureSessionId(null); setCaptureQuick(true); setCaptureOpen(true);
                },
              },
            ].map(({ key, label, Icon, onClick, selected }) => (
              <button
                key={key}
                data-testid={`widget-nav-${key}`}
                data-nav-slot
                onClick={onClick}
                aria-label={label}
                title={label}
                style={{
                  width: 46, border: 'none', cursor: 'pointer',
                  background: selected ? 'rgba(0,0,0,0.08)' : 'transparent',
                  borderRadius: 100,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: selected ? '#1C1C1E' : '#6E6E76',
                }}
              >
                <Icon size={20} strokeWidth={selected ? 2.3 : 2} />
              </button>
            ))}
            {/* apple-design §1/§4: instant press-in, spring-back release. */}
            <style>{`
              [data-nav-slot]{transition:transform .32s cubic-bezier(.34,1.56,.64,1)}
              [data-nav-slot]:active{transform:scale(.88);transition:transform .1s ease-out}
              @media (prefers-reduced-motion: reduce){[data-nav-slot]{transition:none}}
            `}</style>
          </div>
        </div>
      )}

      {/* "Use this Widget" CTA — bottom-center while the render is the
          example/Discovery preview (app `_UseThisWidgetCta`): Example mode
          always; User Data until adopted. Adoption only happens on tap. */}
      {showCta && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 26, zIndex: 30,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
          pointerEvents: 'none', fontFamily: FONT_UI,
        }}>
          {ctaError && (
            <div style={{
              pointerEvents: 'auto', maxWidth: 280, textAlign: 'center',
              fontSize: 11, color: '#E5484D', fontWeight: 600,
              background: 'rgba(255,255,255,0.92)', borderRadius: 8, padding: '4px 10px',
            }}>
              {ctaError}
            </div>
          )}
          {isTemplate && !(!isUser && instance) && (
            <input
              value={templateTitle}
              onChange={(e) => setTemplateTitle(e.target.value)}
              placeholder="Name your collection (e.g. My Birds)"
              style={{
                pointerEvents: 'auto',
                width: 220, padding: '9px 14px', borderRadius: 12,
                border: `1px solid ${T.cell}`, fontSize: 13, fontFamily: FONT_UI,
                background: 'rgba(255,255,255,0.95)', color: T.text,
                outline: 'none', textAlign: 'center',
                boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
              }}
            />
          )}
          <button
            onClick={handleUseThisWidget}
            disabled={instantiateBusy}
            style={{
              pointerEvents: 'auto',
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '12px 22px', borderRadius: 24, border: 'none',
              background: T.accent, opacity: instantiateBusy ? 0.55 : 1,
              color: '#FFFFFF', fontSize: 14, fontWeight: 700, letterSpacing: 0.4,
              boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
              cursor: instantiateBusy ? 'wait' : 'pointer', fontFamily: FONT_UI,
            }}
          >
            {instantiateBusy && (
              <svg width="14" height="14" viewBox="0 0 50 50" aria-hidden>
                <path fill="none" stroke="#FFFFFF" strokeWidth="6" strokeLinecap="round"
                  d="M25 5 a20 20 0 0 1 20 20">
                  <animateTransform attributeName="transform" type="rotate"
                    from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
                </path>
              </svg>
            )}
            {instantiateBusy ? 'Installing…' : 'Use this Widget'}
          </button>
        </div>
      )}

      {/* Host-owned item options sheet (long-press / item_options bridge). */}
      {itemMenu && (
        <ItemOptionsSheet
          title={(() => {
            const e = (activeEntries || []).find((x) => String(x.id) === String(itemMenu.entryId));
            const p = e?.payload || {};
            if (itemMenu.pendingSessionId) return 'this pending item';
            if (itemMenu.gridId && !itemMenu.entryId) return 'this capture';
            return p.name || p.title || p.meal_name || 'this item';
          })()}
          busy={itemMenuBusy}
          onDelete={() => {
            if (itemMenu.pendingSessionId) deletePendingGrid(itemMenu.pendingSessionId, itemMenu.gridId);
            else if (itemMenu.gridId && !itemMenu.entryId) deleteGrid(itemMenu.gridId);
            else deleteItem(itemMenu.entryId);
          }}
          onCancel={() => { if (!itemMenuBusy) setItemMenu(null); }}
        />
      )}
      {(itemMenuBusy || instanceDeleteBusy) && <DeletingOverlay />}

      {/* Confirm un-adopt — the app's `_confirmDeleteWidget` AlertDialog. */}
      {confirmInstanceDelete && (
        <div
          onClick={() => { if (!instanceDeleteBusy) setConfirmInstanceDelete(false); }}
          style={{
            position: 'absolute', inset: 0, zIndex: 65,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, fontFamily: FONT_UI,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'rgba(248,248,250,0.98)', borderRadius: 16,
              padding: '20px 18px 12px', width: 280, maxWidth: '100%',
              boxShadow: '0 16px 48px rgba(0,0,0,0.3)', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 8 }}>
              Delete “{(instance?.title || widget?.title || 'this widget')}”
            </div>
            <div style={{ fontSize: 12.5, color: T.textMut, lineHeight: 1.45, marginBottom: 14 }}>
              This removes the widget from your widgets and permanently deletes
              everything you’ve logged to it. This can’t be undone.
            </div>
            <div style={{
              display: 'flex', borderTop: `0.5px solid ${T.cell}`,
              margin: '0 -18px',
            }}>
              <button
                type="button"
                disabled={instanceDeleteBusy}
                onClick={() => setConfirmInstanceDelete(false)}
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  padding: '12px 0', fontSize: 15, fontWeight: 500,
                  color: T.text, cursor: 'pointer', fontFamily: FONT_UI,
                  borderRight: `0.5px solid ${T.cell}`,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={instanceDeleteBusy}
                onClick={handleDeleteInstance}
                style={{
                  flex: 1, border: 'none', background: 'transparent',
                  padding: '12px 0', fontSize: 15, fontWeight: 700,
                  color: '#E5484D', cursor: 'pointer', fontFamily: FONT_UI,
                  opacity: instanceDeleteBusy ? 0.5 : 1,
                }}
              >
                {instanceDeleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: 'absolute', bottom: 110, left: '50%',
            transform: 'translateX(-50%)', zIndex: 80,
            background: 'rgba(28,28,30,0.92)', color: '#FFFFFF',
            borderRadius: 100, padding: '9px 16px',
            fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
            fontFamily: FONT_UI, pointerEvents: 'none',
          }}
        >
          {toast}
        </div>
      )}

      {isUser && instanceId && (
        <WidgetCaptureOverlay
          open={captureOpen}
          onClose={onCaptureClose}
          instanceId={instanceId}
          widgetId={widgetId}
          widgetTitle={instance?.widget_title || widget?.title || ''}
          hint={skillHint}
          initialImageUrl={null}
          initialNote={capturePrefill}
          continueSessionId={captureSessionId}
          quick={captureQuick}
          initialImages={captureImages}
          onDispatchStarted={onDispatchStarted}
          onDispatchResolved={onDispatchResolved}
          onDispatchFailed={onDispatchFailed}
        />
      )}
      <input
        ref={attachFileRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={onAttachFilePicked}
      />
      <input
        ref={pickMediaFileRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onPickMediaFilePicked}
        onCancel={onPickMediaCanceled}
      />
    </div>
  );
}

// App `_GlassCircle`: 36px dark-glass circle with a white icon — the floating
// chrome primitive (back chevron, "⋯" overflow). Matches the app's
// black-42%-alpha Material circle.
function GlassCircle({ onClick, label, style, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        width: 36, height: 36, borderRadius: '50%',
        border: 'none', background: 'rgba(0,0,0,0.42)', color: '#FFFFFF',
        cursor: 'pointer', padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// Full-cover loading overlay while a per-item delete is in flight.
function DeletingOverlay() {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.42)', backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT_UI,
      }}
    >
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        background: 'rgba(28,28,30,0.92)', borderRadius: 16, padding: '22px 26px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      }}>
        <svg width="34" height="34" viewBox="0 0 50 50" aria-label="Deleting">
          <circle cx="25" cy="25" r="20" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="5" />
          <path fill="none" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="round"
            d="M25 5 a20 20 0 0 1 20 20">
            <animateTransform attributeName="transform" type="rotate"
              from="0 25 25" to="360 25 25" dur="0.8s" repeatCount="indefinite" />
          </path>
        </svg>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Deleting…</div>
      </div>
    </div>
  );
}

// Host-drawn iOS-style action sheet for per-item options. The destructive
// Delete row IS the confirm (standard action-sheet pattern).
function ItemOptionsSheet({ title, busy, onDelete, onCancel }) {
  return (
    <div
      onClick={busy ? undefined : onCancel}
      style={{
        position: 'absolute', inset: 0, zIndex: 60,
        background: 'rgba(0,0,0,0.32)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        padding: 10, fontFamily: FONT_UI,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <div style={{
          background: 'rgba(248,248,250,0.96)', backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)', borderRadius: 14, overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px', textAlign: 'center', fontSize: 12,
            color: T.textMut, borderBottom: `0.5px solid ${T.cell}`,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {String(title || 'this item')}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onDelete}
            style={{
              width: '100%', border: 'none', background: 'transparent',
              padding: '15px 16px', fontSize: 17, fontWeight: 600,
              color: '#FF3B30', cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.5 : 1, fontFamily: FONT_UI,
            }}
          >
            {busy ? 'Deleting…' : 'Delete'}
          </button>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          style={{
            width: '100%', border: 'none', borderRadius: 14,
            background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            padding: '15px 16px', fontSize: 17, fontWeight: 700,
            color: T.text, cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.5 : 1, fontFamily: FONT_UI,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function SessionList({ sessions, widgetTitle, onOpen, onDelete }) {
  if (!sessions || sessions.length === 0) {
    return (
      <div style={{
        height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 32, color: T.textMut, fontSize: 13, textAlign: 'center',
      }}>
        No sessions yet — tap "+" in the widget to add your first capture.
      </div>
    );
  }
  return (
    <div style={{
      height: '100%', overflowY: 'auto',
      padding: '16px 12px 140px',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(sessions || []).map((s) => {
          const id = s.id || s.session_id;
          // The title falls back to the RAW prompt, which may wrap hidden
          // <vi_prefix>/<vi_suffix> instruction blocks — this list is a
          // user-visible widget surface, so show only the text outside them.
          // A fully-wrapped prompt strips to '' and takes the generic label.
          const titleStr = stripHiddenTags(s.title || (s.prompt && s.prompt.trim()) || '')
            || `${widgetTitle || 'Session'} capture`;
          // Transient "Recording…" placeholder (capture just dispatched —
          // "recording" as in logging a row, hence the memo icon, NOT a mic):
          // not openable, not deletable — it reconciles into the real row.
          const recording = !!s.recording;
          return (
            <SessionRow
              key={id}
              icon={recording ? '📝' : '💬'}
              title={titleStr}
              subtitle={s.status || ''}
              timestamp={formatRelative(s.created_at || s.createdAt || s.started_at || s.startedAt)}
              statusColor={recording ? T.warn : statusColor(s.status)}
              onOpen={recording ? null : () => onOpen(s)}
              onDelete={(onDelete && !recording) ? () => onDelete(s) : null}
            />
          );
        })}
      </div>
    </div>
  );
}

function SessionRow({ icon, title, subtitle, timestamp, statusColor: statusColorVal, onOpen, onDelete }) {
  const [dx, setDx] = useState(0);
  const [committed, setCommitted] = useState(false);
  const startRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const REVEAL = 88;
  const SNAP = 56;

  const onPointerDown = (e) => {
    if (!onDelete) return;
    startRef.current = { x: e.clientX, dx };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!startRef.current) return;
    const delta = e.clientX - startRef.current.x;
    let next = startRef.current.dx + delta;
    if (next > 0) next = 0;
    if (next < -REVEAL - 20) next = -REVEAL - 20;
    setDx(next);
  };
  const onPointerUp = () => {
    setDragging(false);
    if (!startRef.current) { setDx(committed ? -REVEAL : 0); return; }
    startRef.current = null;
    if (-dx >= SNAP) {
      setDx(-REVEAL);
      setCommitted(true);
    } else {
      setDx(0);
      setCommitted(false);
    }
  };
  const handleClick = (e) => {
    if (committed) {
      e.stopPropagation();
      setDx(0);
      setCommitted(false);
      return;
    }
    onOpen?.();
  };

  return (
    <div
      style={{
        position: 'relative', overflow: 'hidden', borderRadius: 14,
        background: T.card, border: `1px solid ${T.cell}`,
      }}
    >
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          aria-label="Delete session"
          style={{
            position: 'absolute', right: 0, top: 0, bottom: 0,
            width: REVEAL, border: 'none', cursor: 'pointer',
            background: '#FF453A', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, fontFamily: FONT_UI, fontSize: 12, fontWeight: 600,
          }}
        >
          <Trash2 size={16} strokeWidth={2.2} />
          Delete
        </button>
      )}
      <button
        type="button"
        onClick={handleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          width: '100%',
          textAlign: 'left', background: T.card,
          border: 'none', borderRadius: 14,
          padding: '12px 14px', cursor: 'pointer',
          display: 'flex', gap: 12, alignItems: 'center',
          fontFamily: FONT_UI, color: T.text,
          transform: `translateX(${dx}px)`,
          transition: dragging ? 'none' : 'transform 180ms ease-out',
          position: 'relative', zIndex: 1,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 40, height: 40, borderRadius: 12, flexShrink: 0,
            background: T.accentLight, color: T.text,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22,
          }}
        >
          {icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{
              fontSize: 14, fontWeight: 600, flex: 1,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {title}
            </span>
            <span style={{ fontSize: 11, color: T.textMut, flexShrink: 0 }}>
              {timestamp}
            </span>
          </div>
          <div style={{
            display: 'flex', gap: 6, alignItems: 'center', marginTop: 2,
            fontSize: 12, color: T.textMut,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {subtitle ? (
              <>
                <span style={{
                  width: 6, height: 6, borderRadius: 6,
                  background: statusColorVal, flexShrink: 0,
                }} />
                <span>{subtitle}</span>
              </>
            ) : null}
          </div>
        </div>
      </button>
    </div>
  );
}
